import React, { useState, useEffect, useCallback } from 'react'
import {
  TrendingUp, TrendingDown, RefreshCw, BarChart2, Target,
  Zap, ShieldCheck, ChevronUp, ChevronDown, Minus,
  Award, AlertTriangle, CheckCircle, Clock, Info, BookOpen,
  RotateCw, Calendar, Lightbulb, ArrowDownCircle, ArrowUpCircle, Globe,
} from 'lucide-react'
import { mfIntelAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import MFCategoryRankings from '../components/MFCategoryRankings'
import MFPortfolioValidation from '../components/MFPortfolioValidation'
import MFReplacementAudit from '../components/MFReplacementAudit'

// ── Formatters ────────────────────────────────────────────────────────────────
const fmt   = (n, d = 2) => n == null || isNaN(n) ? '—' : Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d })
const fmtCr = (n) => n == null ? '—' : `₹${fmt(n)}`
const pct   = (n) => n == null ? '—' : `${n > 0 ? '+' : ''}${fmt(n)}%`
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v ?? 0))

const gainColor  = (n) => n == null ? 'text-gray-500' : n >= 0 ? 'text-emerald-600' : 'text-red-500'
const scoreColor = (s) => s >= 70 ? 'text-emerald-600' : s >= 45 ? 'text-amber-500' : 'text-red-500'
const scoreBg    = (s) => s >= 70 ? 'bg-emerald-500' : s >= 45 ? 'bg-amber-400' : 'bg-red-500'

const labelBadge = {
  Consistent:   'bg-emerald-100 text-emerald-700',
  Improving:    'bg-blue-100 text-blue-700',
  Decaying:     'bg-red-100 text-red-700',
  Erratic:      'bg-amber-100 text-amber-700',
  Stable:       'bg-emerald-100 text-emerald-700',
  Volatile:     'bg-amber-100 text-amber-700',
  Insufficient: 'bg-gray-100 text-gray-500',
}

// ── Small helpers ─────────────────────────────────────────────────────────────
const ScoreBar = ({ value, className = '' }) => (
  <div className={`h-1.5 w-full bg-gray-100 rounded-full overflow-hidden ${className}`}>
    <div className={`h-full rounded-full ${scoreBg(value ?? 0)}`} style={{ width: `${clamp(value, 0, 100)}%` }} />
  </div>
)

const ScorePill = ({ value, label }) => (
  <div className="flex items-center gap-1.5">
    <span className={`text-sm font-semibold ${scoreColor(value ?? 0)}`}>{value != null ? Math.round(value) : '—'}</span>
    {label && <span className="text-xs text-gray-400">{label}</span>}
  </div>
)

const HorizBadge = ({ data, label }) => {
  if (!data) return <span className="text-xs text-gray-300">—</span>
  const pctile = data.percentile ?? 0
  return (
    <div className="text-center">
      <div className={`text-xs font-semibold ${scoreColor(pctile)}`}>#{data.rank}</div>
      <div className="text-[10px] text-gray-400">{label}</div>
    </div>
  )
}

const Label = ({ text }) => (
  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${labelBadge[text] ?? 'bg-gray-100 text-gray-500'}`}>
    {text ?? '—'}
  </span>
)

// ── Summary card ──────────────────────────────────────────────────────────────
const SummaryCard = ({ title, value, sub, color = 'text-gray-800', icon: Icon }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
    {Icon && (
      <div className="w-9 h-9 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-primary-600" />
      </div>
    )}
    <div>
      <p className="text-xs text-gray-500 uppercase tracking-wide">{title}</p>
      <p className={`text-lg font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
)

// ── Analysis progress banner ──────────────────────────────────────────────────
// Persists across tab switches because it lives above the tab bar.
// step/totalSteps drive the progress bar.
const AnalysisBanner = ({ running, status, step, total }) => {
  if (!status) return null
  const isDone  = status === 'Done ✓'
  const isError = status.startsWith('Error:')
  const pct     = total > 0 ? Math.round((step / total) * 100) : null

  return (
    <div className={`rounded-xl border px-4 py-3 transition-all ${
      isDone  ? 'bg-emerald-50 border-emerald-200' :
      isError ? 'bg-red-50    border-red-200'      :
                'bg-primary-50 border-primary-200'
    }`}>
      <div className="flex items-center gap-3">
        {running  && <RefreshCw   className="w-4 h-4 text-primary-600 animate-spin flex-shrink-0" />}
        {isDone   && <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
        {isError  && <AlertTriangle className="w-4 h-4 text-red-600  flex-shrink-0" />}

        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium leading-none ${
            isDone ? 'text-emerald-700' : isError ? 'text-red-700' : 'text-primary-800'
          }`}>{status}</p>
          {running && (
            <p className="text-xs text-primary-500 mt-0.5">You can switch tabs — analysis continues in background</p>
          )}
        </div>

        {pct != null && running && (
          <span className="text-xs font-semibold text-primary-600 flex-shrink-0 tabular-nums">
            {pct}%
          </span>
        )}
      </div>

      {/* Progress bar */}
      {(running || isDone) && pct != null && (
        <div className="mt-2 h-1 bg-primary-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${isDone ? 'bg-emerald-500' : 'bg-primary-500'}`}
            style={{ width: `${isDone ? 100 : pct}%` }}
          />
        </div>
      )}
    </div>
  )
}

// ── Sync panel (controlled — state lives in parent MutualFunds) ────────────────
const SyncPanel = ({ running, onRun }) => (
  <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-4 flex items-center justify-between gap-4">
    <div>
      <p className="text-sm font-medium text-gray-800">Full Analysis</p>
      <p className="text-xs text-gray-500 mt-0.5">
        Scheme universe → NAV history → scores → ranks → persistence
      </p>
    </div>
    <button
      onClick={onRun}
      disabled={running}
      className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
    >
      <RefreshCw className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} />
      {running ? 'Running…' : 'Run Now'}
    </button>
  </div>
)

// ── Holdings tab ──────────────────────────────────────────────────────────────
const HoldingsTab = ({ holdings, summary, loading }) => {
  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>
  if (!holdings?.length) return (
    <div className="text-center py-16 text-gray-400">
      <BarChart2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
      <p className="font-medium">No holdings found</p>
      <p className="text-sm mt-1">Add MF transactions in the Portfolio tab → Mutual Funds</p>
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Category breakdown */}
      {summary?.byCategory && Object.keys(summary.byCategory).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(summary.byCategory)
            .sort((a, b) => b[1].currentValue - a[1].currentValue)
            .map(([cat, d]) => (
              <div key={cat} className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 truncate">{cat}</p>
                <p className="text-sm font-semibold text-gray-800 mt-0.5">{fmtCr(d.currentValue)}</p>
                <p className={`text-xs font-medium mt-0.5 ${gainColor(d.gainLossPct)}`}>
                  {pct(d.gainLossPct)}
                </p>
              </div>
            ))}
        </div>
      )}

      {/* Holdings table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Fund</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Units</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Invested</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Current</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">P&L</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">NAV</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {holdings.map((h) => (
              <tr key={h.groupKey} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-800 truncate max-w-[220px]" title={h.schemeName}>
                    {h.schemeName || h.fundName}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {h.amc && <span className="text-xs text-gray-400">{h.amc}</span>}
                    {h.category && <span className="text-[11px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{h.category}</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-right text-gray-700">{fmt(h.totalUnits, 4)}</td>
                <td className="px-4 py-3 text-right text-gray-700">{fmtCr(h.totalInvested)}</td>
                <td className="px-4 py-3 text-right font-medium text-gray-800">
                  {h.currentValue != null ? fmtCr(h.currentValue) : <span className="text-gray-300">No NAV</span>}
                </td>
                <td className={`px-4 py-3 text-right font-semibold ${gainColor(h.gainLoss)}`}>
                  {h.gainLoss != null ? (
                    <div>
                      <div>{pct(h.gainLossPct)}</div>
                      <div className="text-xs font-normal">{h.gainLoss >= 0 ? '+' : ''}{fmtCr(h.gainLoss)}</div>
                    </div>
                  ) : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="text-gray-700">{h.currentNav != null ? `₹${fmt(h.currentNav, 2)}` : '—'}</div>
                  {h.currentNavDate && <div className="text-[10px] text-gray-400">{h.currentNavDate}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Scores tab ────────────────────────────────────────────────────────────────
const ScoresTab = ({ holdingScores, loading }) => {
  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>

  const scored = holdingScores?.filter(h => h.score)
  const unscored = holdingScores?.filter(h => !h.score)

  if (!holdingScores?.length) return (
    <div className="text-center py-16 text-gray-400">
      <BarChart2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
      <p className="font-medium">No score data yet</p>
      <p className="text-sm mt-1">Click "Run Full Analysis" to compute scores</p>
    </div>
  )

  return (
    <div className="space-y-4">
      {unscored?.length > 0 && (
        <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-2 text-xs text-amber-700">
          {unscored.length} fund{unscored.length > 1 ? 's' : ''} not yet scored — run analysis to include them
        </div>
      )}
      <div className="space-y-3">
        {scored?.map((h) => {
          const s = h.score
          return (
            <div key={h.schemeCode} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 truncate">{h.schemeName || h.fundName}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{h.category} · {h.subCategory}</p>
                </div>
                <div className="flex-shrink-0 text-center">
                  <div className={`text-2xl font-bold ${scoreColor(s.compositeScore)}`}>{Math.round(s.compositeScore)}</div>
                  <div className="text-[10px] text-gray-400">/ 100</div>
                  <div className={`text-[11px] mt-0.5 font-medium ${
                    s.dataQuality === 'GOOD' ? 'text-emerald-600' : s.dataQuality === 'PARTIAL' ? 'text-amber-500' : 'text-red-400'
                  }`}>{s.dataQuality}</div>
                </div>
              </div>

              {/* Sub-score bars */}
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2">
                {[
                  ['1Y Return',    s.returns1YScore],
                  ['3Y Return',    s.returns3YScore],
                  ['Momentum',     s.momentumScore],
                  ['Consistency',  s.consistencyScore],
                  ['Sharpe',       s.sharpeScore],
                  ['Volatility',   s.volatilityScore],
                  ['Drawdown',     s.drawdownScore],
                  ['Cat. Rank',    s.categoryRankScore],
                ].map(([label, val]) => (
                  <div key={label}>
                    <div className="flex justify-between text-[11px] mb-0.5">
                      <span className="text-gray-500">{label}</span>
                      <span className={`font-medium ${scoreColor(val ?? 0)}`}>{val != null ? Math.round(val) : '—'}</span>
                    </div>
                    <ScoreBar value={val} />
                  </div>
                ))}
              </div>

              {/* Raw factors summary */}
              {s.factors && (
                <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-gray-500 border-t border-gray-50 pt-2">
                  {s.factors.return1Y   != null && <span>1Y <b>{pct(s.factors.return1Y)}</b></span>}
                  {s.factors.return3YCAGR != null && <span>3Y CAGR <b>{pct(s.factors.return3YCAGR)}</b></span>}
                  {s.factors.annualisedVol != null && <span>Vol <b>{fmt(s.factors.annualisedVol)}%</b></span>}
                  {s.factors.sharpeRatio  != null && <span>Sharpe <b>{fmt(s.factors.sharpeRatio)}</b></span>}
                  {s.factors.maxDrawdown  != null && <span>MaxDD <b>{fmt(s.factors.maxDrawdown)}%</b></span>}
                  {s.factors.navCount     != null && <span><b>{s.factors.navCount}</b> NAV records</span>}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Opportunities tab ─────────────────────────────────────────────────────────
const OpportunitiesTab = ({ opportunities, loading }) => {
  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>
  if (!opportunities?.length) return (
    <div className="text-center py-16 text-gray-400">
      <Zap className="w-10 h-10 mx-auto mb-3 opacity-40" />
      <p className="font-medium">No opportunity data yet</p>
      <p className="text-sm mt-1">Click "Run Full Analysis" to compute opportunity scores</p>
    </div>
  )

  const COMP_LABELS = {
    rankMomentum:    { label: 'Rank Momentum',    desc: 'Recent horizons better than historical' },
    persistence:     { label: 'Persistence',      desc: 'Rank trend improving over time' },
    consistency:     { label: 'Consistency',      desc: 'Repeatable edge, not a one-month spike' },
    drawdownDefense: { label: 'Drawdown Defense', desc: 'Downside protection vs peers' },
    categoryAlpha:   { label: 'Category Alpha',   desc: 'Beating the category on returns' },
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 px-1">
        Opportunity score surfaces funds likely to <b>continue</b> outperforming — not funds that already have.
      </p>
      {opportunities.map((h, i) => (
        <div key={h.schemeCode ?? i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-medium text-gray-800 truncate">{h.schemeName || h.fundName}</p>
              <p className="text-xs text-gray-400 mt-0.5">{h.category} · {h.subCategory}</p>
              {h.totalInvested != null && (
                <p className="text-xs text-gray-500 mt-1">
                  Invested: {fmtCr(h.totalInvested)}
                  {h.gainLossPct != null && <span className={` ml-2 font-medium ${gainColor(h.gainLossPct)}`}>{pct(h.gainLossPct)}</span>}
                </p>
              )}
            </div>
            <div className="flex-shrink-0 text-center">
              <div className={`text-2xl font-bold ${scoreColor(h.opportunityScore)}`}>
                {h.opportunityScore != null ? Math.round(h.opportunityScore) : '—'}
              </div>
              <div className="text-[10px] text-gray-400">Opportunity</div>
            </div>
          </div>

          {h.components && (
            <div className="mt-3 space-y-1.5">
              {Object.entries(h.components).map(([key, val]) => {
                const meta = COMP_LABELS[key]
                if (!meta) return null
                return (
                  <div key={key} className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-500 w-32 flex-shrink-0">{meta.label}</span>
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${scoreBg(val ?? 0)}`} style={{ width: `${clamp(val, 0, 100)}%` }} />
                    </div>
                    <span className={`text-[11px] font-medium w-6 text-right ${scoreColor(val ?? 0)}`}>{val != null ? Math.round(val) : '—'}</span>
                  </div>
                )
              })}
            </div>
          )}

          {h.signals && (
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.values(h.signals).filter(Boolean).map((sig, j) => (
                <span key={j} className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                  ['Rising', 'Improving', 'High', 'Protected', 'Outperforming'].includes(sig)
                    ? 'bg-emerald-50 text-emerald-700'
                    : ['Falling', 'Decaying', 'Low', 'Exposed', 'Lagging'].includes(sig)
                      ? 'bg-red-50 text-red-600'
                      : 'bg-gray-100 text-gray-500'
                }`}>{sig}</span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── Benchmark tab ─────────────────────────────────────────────────────────────
const BenchmarkTab = ({ holdingsBenchmark, loading }) => {
  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>
  if (!holdingsBenchmark?.length) return (
    <div className="text-center py-16 text-gray-400">
      <Target className="w-10 h-10 mx-auto mb-3 opacity-40" />
      <p className="font-medium">No benchmark data yet</p>
      <p className="text-sm mt-1">Click "Load Benchmark" to compute alpha vs category benchmark</p>
    </div>
  )

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 px-1">
        Evaluates each fund against its benchmark. Alpha = fund return minus category/ETF proxy return.
      </p>
      {holdingsBenchmark.map((b, i) => (
        <div key={b.schemeCode ?? i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-medium text-gray-800 truncate">{b.schemeName}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Benchmark: <span className="text-gray-600">{b.benchmarkName}</span>
                <span className={`ml-2 text-[11px] px-1.5 py-0.5 rounded font-medium ${
                  b.benchmarkType === 'ETF_Proxy' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'
                }`}>{b.benchmarkType}</span>
              </p>
            </div>
            <div className="flex-shrink-0 flex items-center gap-3">
              {b.beatBenchmark1Y != null && (
                b.beatBenchmark1Y
                  ? <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded-lg font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" />Beat</span>
                  : <span className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded-lg font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Lagged</span>
              )}
              <div className="text-center">
                <div className={`text-lg font-bold ${gainColor(b.alpha1Y)}`}>
                  {b.alpha1Y != null ? (b.alpha1Y > 0 ? '+' : '') + fmt(b.alpha1Y) + '%' : '—'}
                </div>
                <div className="text-[10px] text-gray-400">1Y Alpha</div>
              </div>
            </div>
          </div>

          {/* Period returns */}
          {b.periods && Object.keys(b.periods).length > 0 && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(b.periods).map(([period, d]) => (
                <div key={period} className="bg-gray-50 rounded-lg p-2 text-center">
                  <div className="text-[10px] text-gray-400 mb-1">{period}</div>
                  <div className="text-xs text-gray-600">Fund: <span className={`font-semibold ${gainColor(d.fundReturn)}`}>{pct(d.fundReturn)}</span></div>
                  {d.benchReturn != null && <div className="text-xs text-gray-400">Bench: {pct(d.benchReturn)}</div>}
                  {d.alpha != null && <div className={`text-xs font-semibold mt-0.5 ${gainColor(d.alpha)}`}>α {d.alpha > 0 ? '+' : ''}{fmt(d.alpha)}%</div>}
                </div>
              ))}
            </div>
          )}

          {/* Monthly stats */}
          {b.monthly && (
            <div className="mt-2 flex flex-wrap gap-4 text-[11px] text-gray-500 border-t border-gray-50 pt-2">
              {b.monthly.informationRatio   != null && <span>IR: <b className={gainColor(b.monthly.informationRatio)}>{fmt(b.monthly.informationRatio)}</b></span>}
              {b.monthly.beatBenchmarkMonthsPct != null && <span>Beat benchmark: <b>{fmt(b.monthly.beatBenchmarkMonthsPct)}%</b> of months</span>}
              {b.monthly.beta               != null && <span>Beta: <b>{fmt(b.monthly.beta)}</b></span>}
              {b.monthly.dataMonths         != null && <span>{b.monthly.dataMonths} months of data</span>}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── Health tab ────────────────────────────────────────────────────────────────
const HEALTH_LABEL_STYLE = {
  Healthy: { bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  Watch:   { bg: 'bg-amber-50',    text: 'text-amber-700',   border: 'border-amber-200',   dot: 'bg-amber-400'   },
  Weak:    { bg: 'bg-orange-50',   text: 'text-orange-700',  border: 'border-orange-200',  dot: 'bg-orange-400'  },
  Replace: { bg: 'bg-red-50',      text: 'text-red-700',     border: 'border-red-200',     dot: 'bg-red-500'     },
}

const REASON_LABELS = {
  LOW_CATEGORY_RANK:    { text: 'Low category rank',       color: 'bg-red-50 text-red-600'      },
  NEGATIVE_ALPHA:       { text: 'Negative alpha',          color: 'bg-red-50 text-red-600'      },
  LOW_CONSISTENCY:      { text: 'Inconsistent returns',    color: 'bg-amber-50 text-amber-700'  },
  HIGH_DRAWDOWN:        { text: 'High drawdown',           color: 'bg-orange-50 text-orange-700'},
  DECAYING_PERSISTENCE: { text: 'Rank declining over time',color: 'bg-red-50 text-red-600'      },
  HIGH_EXPENSE_RATIO:   { text: 'High expense ratio',      color: 'bg-amber-50 text-amber-700'  },
  STRONG_ALPHA:         { text: 'Strong alpha',            color: 'bg-emerald-50 text-emerald-700'},
  TOP_QUARTILE:         { text: 'Top quartile',            color: 'bg-emerald-50 text-emerald-700'},
  INSUFFICIENT_DATA:    { text: 'Limited data',            color: 'bg-gray-100 text-gray-500'   },
}

const COMP_META_HEALTH = {
  categoryRank: 'Category Rank',
  alpha:        'Alpha',
  consistency:  'Consistency',
  drawdown:     'Drawdown',
  persistence:  'Persistence',
  expenseRatio: 'Expense Ratio',
}

const HealthTab = ({ holdingsHealth, loading, onRunHealth }) => {
  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>

  const hasData = holdingsHealth?.some(h => h.health)

  if (!holdingsHealth?.length) return (
    <div className="text-center py-16 text-gray-400">
      <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-40" />
      <p className="font-medium">No health data yet</p>
      <p className="text-sm mt-1">Click "Run Health Check" to assess all your funds</p>
      <button onClick={onRunHealth} className="mt-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors">
        Run Health Check
      </button>
    </div>
  )

  if (!hasData) return (
    <div className="text-center py-12 text-gray-400 space-y-3">
      <ShieldCheck className="w-10 h-10 mx-auto opacity-40" />
      <p className="font-medium">Health not yet computed for your funds</p>
      <p className="text-sm">Run analysis first, then come back here</p>
      <button onClick={onRunHealth} className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors">
        Run Health Check
      </button>
    </div>
  )

  // Summary bar
  const counts = { Healthy: 0, Watch: 0, Weak: 0, Replace: 0 }
  holdingsHealth.forEach(h => { if (h.health?.healthLabel) counts[h.health.healthLabel]++ })

  return (
    <div className="space-y-4">
      {/* Label summary strip */}
      <div className="grid grid-cols-4 gap-2">
        {Object.entries(counts).map(([lbl, n]) => {
          const s = HEALTH_LABEL_STYLE[lbl]
          return (
            <div key={lbl} className={`rounded-xl border p-3 text-center ${s.bg} ${s.border}`}>
              <div className={`text-2xl font-bold ${s.text}`}>{n}</div>
              <div className={`text-xs font-medium mt-0.5 ${s.text}`}>{lbl}</div>
            </div>
          )
        })}
      </div>

      {/* Per-fund cards */}
      {holdingsHealth.map((h) => {
        const health = h.health
        const lbl    = health?.healthLabel ?? 'Watch'
        const s      = HEALTH_LABEL_STYLE[lbl]

        return (
          <div key={h.schemeCode} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${s.border}`}>
            {/* Header stripe */}
            <div className={`h-1 w-full ${s.dot}`} />
            <div className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 truncate">{h.schemeName}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{h.category} · {h.subCategory}</p>
                  {h.totalInvested != null && (
                    <p className="text-xs text-gray-500 mt-1">
                      {fmtCr(h.totalInvested)} invested
                      {h.gainLossPct != null && (
                        <span className={`ml-2 font-medium ${gainColor(h.gainLossPct)}`}>{pct(h.gainLossPct)}</span>
                      )}
                    </p>
                  )}
                </div>

                <div className="flex-shrink-0 flex flex-col items-end gap-1">
                  {/* Health label badge */}
                  <span className={`text-sm font-bold px-3 py-1 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
                    {lbl}
                  </span>
                  {health?.healthScore != null && (
                    <span className="text-xs text-gray-400">{Math.round(health.healthScore)} / 100</span>
                  )}
                </div>
              </div>

              {health ? (
                <>
                  {/* Component score bars */}
                  {health.components && (
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
                      {Object.entries(health.components).map(([key, comp]) => {
                        if (!comp || comp.score == null) return null
                        return (
                          <div key={key}>
                            <div className="flex justify-between text-[11px] mb-0.5">
                              <span className="text-gray-500">{COMP_META_HEALTH[key] ?? key}</span>
                              <span className={`font-medium ${scoreColor(comp.score)}`}>{Math.round(comp.score)}</span>
                            </div>
                            <ScoreBar value={comp.score} />
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {/* Reason chips */}
                  {health.reasonCodes?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {health.reasonCodes.map(code => {
                        const r = REASON_LABELS[code]
                        return r ? (
                          <span key={code} className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${r.color}`}>
                            {r.text}
                          </span>
                        ) : null
                      })}
                    </div>
                  )}

                  {/* Recommendation */}
                  {health.recommendation && (
                    <div className="mt-3 flex gap-2 bg-gray-50 rounded-lg p-2.5">
                      <Info className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <p className="text-[11px] text-gray-600 leading-relaxed">{health.recommendation}</p>
                    </div>
                  )}

                  <div className="mt-2 flex justify-between items-center text-[10px] text-gray-400">
                    <span>{health.componentsAvailable ?? 0} of 6 signals available · {health.dataQuality}</span>
                    {health.date && <span>{health.date}</span>}
                  </div>
                </>
              ) : (
                <p className="mt-3 text-xs text-gray-400">No health data — run analysis to compute</p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}


// ── Exposure tab ──────────────────────────────────────────────────────────────
const ExposureTab = ({ exposure, status, loading, onImport }) => {
  const [importing,  setImporting]  = useState(false)
  const [selCode,    setSelCode]    = useState('')
  const [month,      setMonth]      = useState(new Date().toISOString().slice(0, 7))
  const [csvText,    setCsvText]    = useState('')
  const [showImport, setShowImport] = useState(false)

  const handleImport = async () => {
    if (!selCode || !csvText.trim()) return
    setImporting(true)
    try {
      await mfIntelAPI.importPortfolio(selCode, { csvText, month })
      toast.success('Portfolio imported — reload exposure')
      setShowImport(false)
      setCsvText('')
      onImport?.()
    } catch (err) {
      toast.error(err.response?.data?.error ?? 'Import failed')
    } finally { setImporting(false) }
  }

  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>

  const missingFunds = status?.filter(f => !f.hasData) ?? []
  const hasExposure  = exposure?.stocks?.length > 0

  return (
    <div className="space-y-4">
      {/* Data status panel */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-700">Fund Portfolio Data</p>
          <button onClick={() => setShowImport(v => !v)}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium">
            {showImport ? 'Cancel' : '+ Import CSV'}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(status ?? []).map(f => (
            <div key={f.schemeCode} className={`flex items-center gap-2 p-2 rounded-lg text-xs ${f.hasData ? 'bg-emerald-50' : 'bg-gray-50'}`}>
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${f.hasData ? 'bg-emerald-500' : 'bg-gray-300'}`} />
              <div className="min-w-0">
                <p className="font-medium text-gray-700 truncate">{f.schemeName}</p>
                <p className="text-gray-400">
                  {f.hasData ? `${f.stocksLoaded} stocks · ${f.portfolioMonth}` : 'No portfolio data'}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CSV Import panel */}
        {showImport && (
          <div className="border-t border-gray-100 pt-3 space-y-2">
            <p className="text-xs text-gray-500">
              Download your fund's monthly portfolio disclosure from the AMC website (CSV format),
              paste the content below. The parser handles most AMC formats automatically.
            </p>
            <div className="flex gap-2">
              <select value={selCode} onChange={e => setSelCode(e.target.value)}
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
                <option value="">Select fund…</option>
                {(status ?? []).map(f => (
                  <option key={f.schemeCode} value={f.schemeCode}>{f.schemeName}</option>
                ))}
              </select>
              <input type="month" value={month} onChange={e => setMonth(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5" />
            </div>
            <textarea
              value={csvText} onChange={e => setCsvText(e.target.value)}
              placeholder="Paste CSV content here (headers + rows)…"
              className="w-full text-xs border border-gray-200 rounded-lg p-2 h-32 font-mono resize-none"
            />
            <button onClick={handleImport} disabled={importing || !selCode || !csvText.trim()}
              className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors">
              {importing ? 'Importing…' : 'Import Portfolio'}
            </button>
          </div>
        )}
      </div>

      {/* Summary */}
      {exposure?.summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCard title="Stocks Tracked" value={`${exposure.summary.stockCount}`} icon={BarChart2} />
          <SummaryCard title="Direct Exposure"   value={fmtCr(exposure.summary.totalDirectValue)}   icon={TrendingUp} />
          <SummaryCard title="Indirect (MF)"     value={fmtCr(exposure.summary.totalIndirectValue)} icon={Award} />
          <SummaryCard title="Total Exposure"    value={fmtCr(exposure.summary.totalExposure)}      icon={Target} />
        </div>
      )}

      {/* Missing portfolio warning */}
      {missingFunds.length > 0 && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-700">
          <b>{missingFunds.length} fund{missingFunds.length > 1 ? 's' : ''}</b> without portfolio data —
          indirect exposure via these funds is excluded.
          Import CSV above to include them.
        </div>
      )}

      {!hasExposure && !loading && (
        <div className="text-center py-12 text-gray-400">
          <Target className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No exposure data yet</p>
          <p className="text-sm mt-1">
            {status?.every(f => !f.hasData)
              ? 'Import fund portfolio CSVs above to see cross-asset exposure'
              : 'Add direct stock holdings in Portfolio, or import fund portfolios above'}
          </p>
        </div>
      )}

      {/* Exposure table */}
      {hasExposure && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Stock</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Direct</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Via Funds</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Funds</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {exposure.stocks.map(s => (
                <tr key={s.stockSymbol || s.isin} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{s.stockSymbol || s.stockName}</div>
                    {s.stockName && s.stockSymbol && s.stockName !== s.stockSymbol && (
                      <div className="text-xs text-gray-400 truncate max-w-[160px]">{s.stockName}</div>
                    )}
                    {s.sector && <div className="text-[11px] text-gray-400">{s.sector}</div>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {s.directValue > 0
                      ? <span className="text-gray-700 font-medium">{fmtCr(s.directValue)}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {s.indirectValue > 0
                      ? <span className="text-blue-600 font-medium">{fmtCr(s.indirectValue)}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-bold text-gray-800">{fmtCr(s.totalExposure)}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {s.sources.funds.length > 0 && (
                      <div className="flex flex-col items-end gap-0.5">
                        {s.sources.funds.map((f, i) => (
                          <span key={i} className="text-[11px] text-gray-400">{f.weight.toFixed(1)}%</span>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Overlap tab ───────────────────────────────────────────────────────────────
const SEVERITY_STYLE = {
  High:   'bg-red-50 text-red-700 border-red-200',
  Medium: 'bg-amber-50 text-amber-700 border-amber-200',
  Low:    'bg-blue-50 text-blue-700 border-blue-200',
}

const RISK_TYPE_LABELS = {
  HIGH_PAIR_OVERLAP:    'High Fund Overlap',
  STOCK_CONCENTRATION:  'Stock Concentration',
  AMC_CONCENTRATION:    'AMC Concentration',
  STYLE_OVERLAP:        'Style Overlap',
}

// Overlap colour: 0% = green, 25% = amber, 50%+ = red
const overlapColor = (pct) =>
  pct >= 35 ? 'text-red-600' : pct >= 20 ? 'text-amber-500' : 'text-emerald-600'

const overlapBg = (pct) =>
  pct >= 35 ? 'bg-red-400' : pct >= 20 ? 'bg-amber-400' : 'bg-emerald-400'

const OverlapBar = ({ value }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${overlapBg(value)}`} style={{ width: `${Math.min(value, 100)}%` }} />
    </div>
    <span className={`text-xs font-semibold w-10 text-right ${overlapColor(value)}`}>{value}%</span>
  </div>
)

const OverlapTab = ({ overlap, loading, onLoad }) => {
  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>

  if (!overlap) return (
    <div className="text-center py-16 text-gray-400">
      <BarChart2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
      <p className="font-medium text-gray-600">Analyse Fund Overlap</p>
      <p className="text-sm mt-1 max-w-sm mx-auto">
        Detects hidden concentration: same stocks held across multiple funds,
        AMC concentration, style duplication.
      </p>
      <button onClick={onLoad}
        className="mt-5 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors">
        Compute Overlap
      </button>
    </div>
  )

  const { pairwiseOverlaps = [], concentrationRisks = [], portfolioStats = {}, fundsWithoutPortfolio = [] } = overlap

  return (
    <div className="space-y-5">

      {/* Portfolio uniqueness score */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-6">
        <div className="text-center flex-shrink-0">
          <div className={`text-4xl font-bold ${overlapColor(100 - (portfolioStats.portfolioUniqueness ?? 100))}`}>
            {portfolioStats.portfolioUniqueness ?? '—'}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">Uniqueness %</div>
        </div>
        <div className="flex-1 space-y-1 text-sm text-gray-600">
          <p><b>{portfolioStats.fundsAnalysed}</b> funds analysed · <b>{portfolioStats.pairsAnalysed}</b> pairs compared</p>
          <p>Avg weighted overlap: <b className={overlapColor(portfolioStats.avgWeightedOverlap ?? 0)}>{portfolioStats.avgWeightedOverlap ?? 0}%</b></p>
          <p>Highest pair overlap: <b className={overlapColor(portfolioStats.maxWeightedOverlap ?? 0)}>{portfolioStats.maxWeightedOverlap ?? 0}%</b></p>
          {fundsWithoutPortfolio.length > 0 && (
            <p className="text-amber-600 text-xs">{fundsWithoutPortfolio.length} fund{fundsWithoutPortfolio.length > 1 ? 's' : ''} excluded — import portfolio CSV in Exposure tab</p>
          )}
        </div>
      </div>

      {/* Concentration risks */}
      {concentrationRisks.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">⚠ Concentration Risks</p>
          <div className="space-y-2">
            {concentrationRisks.map((r, i) => (
              <div key={i} className={`rounded-xl border p-3 ${SEVERITY_STYLE[r.severity] ?? 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wide">{RISK_TYPE_LABELS[r.type] ?? r.type}</span>
                    <span className={`ml-2 text-[11px] px-1.5 py-0.5 rounded font-medium border ${SEVERITY_STYLE[r.severity]}`}>{r.severity}</span>
                  </div>
                  {r.pctOfPortfolio != null && (
                    <span className="text-sm font-bold flex-shrink-0">{r.pctOfPortfolio}% of portfolio</span>
                  )}
                  {r.weightedOverlapPct != null && (
                    <span className="text-sm font-bold flex-shrink-0">{r.weightedOverlapPct}% overlap</span>
                  )}
                </div>
                <p className="text-sm font-medium mt-1">
                  {r.symbol && <span>{r.symbol} {r.stockName && r.stockName !== r.symbol ? `(${r.stockName})` : ''} · </span>}
                  {r.amc && <span>{r.amc} · </span>}
                  {r.subCategory && <span>{r.subCategory} · </span>}
                  {r.fundA && r.fundB && <span>{r.fundA} ↔ {r.fundB}</span>}
                </p>
                <p className="text-xs mt-0.5 opacity-80">
                  {r.message ?? (r.funds ? `Funds: ${r.funds.join(', ')}` : '')}
                </p>
                {r.funds && r.type === 'STOCK_CONCENTRATION' && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {r.funds.map((f, j) => (
                      <span key={j} className="text-[11px] bg-white bg-opacity-60 px-1.5 py-0.5 rounded">
                        {f.schemeName}: {f.weight}%
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {concentrationRisks.length === 0 && pairwiseOverlaps.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-sm text-emerald-700 font-medium">
          ✓ No significant concentration risks detected
        </div>
      )}

      {/* Pairwise overlap cards */}
      {pairwiseOverlaps.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Fund Pair Overlap</p>
          <div className="space-y-3">
            {pairwiseOverlaps.map((pair, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{pair.fundA.schemeName}</p>
                    <p className="text-[11px] text-gray-400">↕</p>
                    <p className="text-sm font-medium text-gray-800 truncate">{pair.fundB.schemeName}</p>
                  </div>
                  <div className="flex-shrink-0 text-center">
                    <div className={`text-2xl font-bold ${overlapColor(pair.weightedOverlapPct)}`}>
                      {pair.weightedOverlapPct}%
                    </div>
                    <div className="text-[10px] text-gray-400">Effective overlap</div>
                    <div className="flex gap-1 mt-1">
                      {pair.sameAMC && <span className="text-[10px] bg-amber-50 text-amber-600 px-1 rounded">Same AMC</span>}
                      {pair.sameSubCategory && <span className="text-[10px] bg-orange-50 text-orange-600 px-1 rounded">Same style</span>}
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-[11px] text-gray-500 mb-0.5">
                    <span>Weighted stock overlap</span>
                  </div>
                  <OverlapBar value={pair.weightedOverlapPct} />
                  <div className="flex items-center justify-between text-[11px] text-gray-400">
                    <span>Stock overlap (Jaccard): {pair.stockOverlapPct}%</span>
                    <span>Sector overlap: {pair.sectorOverlapPct}%</span>
                    <span>{pair.commonStocksCount} common stocks</span>
                  </div>
                </div>

                {pair.commonStocks?.length > 0 && (
                  <div className="mt-3 border-t border-gray-50 pt-2">
                    <p className="text-[11px] text-gray-400 mb-1.5">Top overlapping stocks</p>
                    <div className="flex flex-wrap gap-1.5">
                      {pair.commonStocks.slice(0, 8).map((s, j) => (
                        <span key={j} className="text-[11px] bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full">
                          {s.symbol || s.name}
                          <span className="text-gray-400 ml-1">{s.weightA}% / {s.weightB}%</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {pairwiseOverlaps.length === 0 && !fundsWithoutPortfolio.length && (
        <div className="text-center py-8 text-gray-400 text-sm">
          Only one fund with portfolio data — need at least 2 to compute overlap
        </div>
      )}
    </div>
  )
}

// ── Fragility tab ─────────────────────────────────────────────────────────────
const FRAG_LABEL_STYLE = {
  Resilient: { color: 'text-emerald-600', bg: 'bg-emerald-50',  border: 'border-emerald-200', dot: 'bg-emerald-500' },
  Watch:     { color: 'text-amber-600',   bg: 'bg-amber-50',    border: 'border-amber-200',   dot: 'bg-amber-400'   },
  Fragile:   { color: 'text-red-600',     bg: 'bg-red-50',      border: 'border-red-200',     dot: 'bg-red-500'     },
}

const DIM_META = {
  concentration: { label: 'Concentration Risk', weight: 20, desc: 'Largest single-stock exposure across all funds' },
  amc:           { label: 'AMC Risk',            weight: 20, desc: 'Largest single fund house share of portfolio'  },
  category:      { label: 'Category Risk',       weight: 15, desc: 'Spread across Equity / Debt / Hybrid / Gold'  },
  sector:        { label: 'Sector Risk',         weight: 15, desc: 'Max sector concentration (requires Phase 11)' },
  style:         { label: 'Style Risk',          weight: 15, desc: 'Duplicate sub-category funds (e.g. 2× Small Cap)' },
  correlation:   { label: 'Correlation Risk',    weight: 15, desc: 'Average weighted overlap between fund pairs'   },
}

const FragilityGauge = ({ score, label }) => {
  const s = FRAG_LABEL_STYLE[label] ?? FRAG_LABEL_STYLE.Watch
  const pct = Math.max(0, Math.min(100, score ?? 0))
  const r = 54, circ = 2 * Math.PI * r, offset = circ - (pct / 100) * circ
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-36 h-36">
        <svg className="w-36 h-36 -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r={r} fill="none" stroke="#e5e7eb" strokeWidth="10" />
          <circle cx="60" cy="60" r={r} fill="none" strokeWidth="10" strokeLinecap="round"
            stroke="currentColor" className={s.color}
            strokeDasharray={circ} strokeDashoffset={offset} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-3xl font-bold ${s.color}`}>{Math.round(pct)}</span>
          <span className="text-xs text-gray-400">/ 100</span>
        </div>
      </div>
      <span className={`mt-2 text-sm font-bold px-3 py-1 rounded-full border ${s.bg} ${s.color} ${s.border}`}>{label}</span>
    </div>
  )
}

const FragilityTab = ({ fragility, loading, onLoad }) => {
  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>

  if (!fragility) return (
    <div className="text-center py-16 text-gray-400">
      <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-40" />
      <p className="font-medium text-gray-600">Portfolio Fragility Score</p>
      <p className="text-sm mt-1 max-w-sm mx-auto">
        Measures how resilient your portfolio is across 6 risk dimensions —
        concentration, AMC, category, sector, style, and correlation.
      </p>
      <p className="text-xs text-gray-400 mt-2">80+ Resilient · 60-80 Watch · Below 60 Fragile</p>
      <button onClick={onLoad}
        className="mt-5 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors">
        Compute Score
      </button>
    </div>
  )

  const { fragilityScore, fragilityLabel, components = {}, weights = {}, dataCompleteness, inputs = {} } = fragility

  // Actions needed (low-scoring dimensions)
  const actions = Object.entries(components)
    .filter(([, c]) => c.action && c.score < 75)
    .sort((a, b) => a[1].score - b[1].score)

  return (
    <div className="space-y-5">

      {/* Score + breakdown */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <FragilityGauge score={fragilityScore} label={fragilityLabel} />

          <div className="flex-1 w-full space-y-3">
            {Object.entries(DIM_META).map(([key, meta]) => {
              const comp = components[key]
              if (!comp) return null
              return (
                <div key={key}>
                  <div className="flex justify-between items-center text-xs mb-1">
                    <span className="text-gray-600 font-medium flex items-center gap-1">
                      {meta.label}
                      <span className="text-gray-400 font-normal">({meta.weight}%)</span>
                      {comp.estimated && <span className="text-[10px] bg-gray-100 text-gray-400 px-1 rounded">est.</span>}
                    </span>
                    <span className={`font-bold ${scoreColor(comp.score)}`}>{Math.round(comp.score)}</span>
                  </div>
                  <ScoreBar value={comp.score} />
                </div>
              )
            })}
            {dataCompleteness !== 'FULL' && (
              <p className="text-[11px] text-gray-400 pt-1">
                Some dimensions estimated — import fund portfolios (Exposure tab) for full accuracy.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Key inputs snapshot */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: 'Worst Stock',       value: inputs.worstStockPct   != null ? `${inputs.worstStockPct}%`     : 'No data', warn: inputs.worstStockPct   > 10 },
          { label: 'Top AMC share',     value: inputs.maxAmcPct       != null ? `${inputs.maxAmcPct}%`         : 'No data', warn: inputs.maxAmcPct       > 50 },
          { label: 'Asset classes',     value: inputs.distinctCategories != null ? `${inputs.distinctCategories}` : 'No data', warn: inputs.distinctCategories < 2 },
          { label: 'Max sector',        value: inputs.maxSectorPct    != null ? `${inputs.maxSectorPct}%`      : 'No data', warn: inputs.maxSectorPct    > 35 },
          { label: 'Style overlaps',    value: inputs.duplicateSubCats != null ? `${inputs.duplicateSubCats}`  : 'No data', warn: inputs.duplicateSubCats > 0 },
          { label: 'Avg fund overlap',  value: inputs.avgWeightedOverlap != null ? `${inputs.avgWeightedOverlap}%` : 'No data', warn: inputs.avgWeightedOverlap > 20 },
        ].map(({ label, value, warn }) => (
          <div key={label} className={`rounded-lg p-3 border ${warn ? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-100'}`}>
            <p className="text-[11px] text-gray-500">{label}</p>
            <p className={`text-base font-bold mt-0.5 ${warn ? 'text-amber-700' : 'text-gray-700'}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Action items */}
      {actions.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Action Items</p>
          <div className="space-y-2">
            {actions.map(([key, comp]) => (
              <div key={key} className={`rounded-xl border p-3 flex gap-3 items-start ${
                comp.score < 40 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-100'
              }`}>
                <div className={`w-1.5 h-full rounded-full flex-shrink-0 mt-1 ${comp.score < 40 ? 'bg-red-400' : 'bg-amber-400'}`} />
                <div>
                  <p className="text-xs font-semibold text-gray-700">{DIM_META[key]?.label}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{comp.action}</p>
                </div>
                <span className={`ml-auto text-xs font-bold flex-shrink-0 ${scoreColor(comp.score)}`}>{Math.round(comp.score)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {actions.length === 0 && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-sm text-emerald-700 font-medium">
          ✓ All risk dimensions are within healthy ranges
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={onLoad} className="text-xs text-gray-400 hover:text-primary-600 flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Recompute
        </button>
      </div>
    </div>
  )
}

// ── Allocation tab ────────────────────────────────────────────────────────────
const RISK_PROFILES   = ['Conservative', 'Moderate', 'Aggressive']
const REGIMES         = ['Bull', 'Neutral', 'Correction', 'Bear', 'Recovery']
const REGIME_COLORS   = { Bull: 'text-emerald-600', Neutral: 'text-gray-600', Correction: 'text-amber-600', Bear: 'text-red-600', Recovery: 'text-blue-600' }
const CAT_COLORS      = { Equity: 'bg-blue-500', Debt: 'bg-emerald-500', Hybrid: 'bg-purple-500', Gold: 'bg-amber-500', Cash: 'bg-gray-400', Other: 'bg-gray-300' }
const ACTION_COLORS   = { INCREASE: 'text-emerald-700 bg-emerald-50 border-emerald-200', DECREASE: 'text-red-600 bg-red-50 border-red-200', MAINTAIN: 'text-gray-500 bg-gray-50 border-gray-200' }

const AllocationBar = ({ label, targetPct, currentPct, color }) => {
  const gap = targetPct - currentPct
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-700 font-medium">{label}</span>
        <span className="text-gray-500">
          <span className="font-semibold text-gray-800">{currentPct?.toFixed(1)}%</span>
          <span className="text-gray-300 mx-1">→</span>
          <span className={`font-semibold ${gap > 4 ? 'text-emerald-600' : gap < -4 ? 'text-red-500' : 'text-gray-600'}`}>{targetPct?.toFixed(1)}%</span>
        </span>
      </div>
      <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
        {/* Current */}
        <div className={`absolute left-0 top-0 h-full rounded-full opacity-40 ${color}`}
          style={{ width: `${clamp(currentPct, 0, 100)}%` }} />
        {/* Target marker */}
        <div className="absolute top-0 h-full w-0.5 bg-gray-700 opacity-60"
          style={{ left: `${clamp(targetPct, 0, 100)}%` }} />
      </div>
      <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
        <span>Current</span>
        <span>|Target</span>
      </div>
    </div>
  )
}

const AllocationTab = ({ allocation, loading, riskProfile, regime, regimeData, onLoad, onChangeProfile, onChangeRegime }) => {
  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>

  return (
    <div className="space-y-5">
      {/* Auto-detected regime banner */}
      {regimeData && <RegimeBanner regimeData={regimeData} />}

      {/* Controls */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <p className="text-xs text-gray-500 mb-1.5 font-medium">Risk Profile</p>
            <div className="flex gap-1">
              {RISK_PROFILES.map(p => (
                <button key={p} onClick={() => onChangeProfile(p)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                    riskProfile === p
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300'
                  }`}>{p}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1.5 font-medium">
              Market Regime
              {regimeData?.regime && <span className="ml-1 text-primary-600 font-normal">↑ auto-detected: {regimeData.regime}</span>}
            </p>
            <div className="flex gap-1 flex-wrap">
              {REGIMES.map(r => (
                <button key={r} onClick={() => onChangeRegime(r)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors ${
                    regime === r
                      ? `${REGIME_COLORS[r]} border-current bg-opacity-10 bg-current`
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                  }`}>{r}</button>
              ))}
            </div>
          </div>
          <button onClick={onLoad}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors">
            <RefreshCw className="w-3 h-3" /> Recalculate
          </button>
        </div>
      </div>

      {!allocation ? (
        <div className="text-center py-10 text-gray-400">
          <Target className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium text-gray-600">Select a risk profile and click Recalculate</p>
        </div>
      ) : (
        <>
          {/* Summary strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCard title="Portfolio Value"  value={fmtCr(allocation.totalValue)} icon={BarChart2} />
            <SummaryCard title="To Increase"      value={`${allocation.summary.toIncrease} slots`}  icon={TrendingUp} color="text-emerald-600" />
            <SummaryCard title="To Decrease"      value={`${allocation.summary.toDecrease} slots`}  icon={TrendingDown} color="text-red-500" />
            <SummaryCard title="Gap to Fill"      value={fmtCr(allocation.summary.totalGapValue)}   icon={Target} />
          </div>

          {/* Category target vs current bars */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <p className="text-sm font-semibold text-gray-700 mb-4">Target vs Current by Asset Class</p>
            <div className="space-y-4">
              {Object.entries(allocation.categoryTargets ?? {}).map(([cat, targetPct]) => {
                const cur = allocation.currentAllocation?.find(c => c.category === cat)
                const currentPct = cur?.currentPct ?? 0
                return (
                  <AllocationBar
                    key={cat}
                    label={cat}
                    targetPct={targetPct}
                    currentPct={currentPct}
                    color={CAT_COLORS[cat] ?? 'bg-gray-400'}
                  />
                )
              })}
            </div>
            <p className="text-[11px] text-gray-400 mt-3">Solid bar = current · Vertical line = target · Opacity shows current allocation</p>
          </div>

          {/* Action table */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Actions</p>
            <div className="space-y-2">
              {allocation.actions?.map((a, i) => (
                <div key={i} className={`rounded-xl border p-3 ${ACTION_COLORS[a.action]}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded border ${ACTION_COLORS[a.action]}`}>{a.action}</span>
                        <span className="text-sm font-semibold text-gray-800">{a.subCategory || a.category}</span>
                        {a.subCategory && <span className="text-[11px] text-gray-400">{a.category}</span>}
                      </div>
                      {a.action !== 'MAINTAIN' && (
                        <p className="text-xs text-gray-600 mt-1">
                          Current <b>{a.currentPct}%</b> → Target <b>{a.targetPct}%</b>
                          {a.gapValue != null && a.gapValue !== 0 && (
                            <span className={`ml-2 font-semibold ${a.gapValue > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                              {a.gapValue > 0 ? '+' : ''}{fmtCr(a.gapValue)}
                            </span>
                          )}
                        </p>
                      )}
                      {a.suggestion && (
                        <p className="text-[11px] text-gray-500 mt-1">
                          Consider: <span className="font-medium text-primary-700">{a.suggestion.schemeName}</span>
                          <span className="text-gray-400"> ({a.suggestion.amc}) · Score {a.suggestion.compositeScore}</span>
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <div className={`text-lg font-bold ${
                        a.gapPct > 4 ? 'text-emerald-600' : a.gapPct < -4 ? 'text-red-500' : 'text-gray-400'
                      }`}>
                        {a.gapPct > 0 ? '+' : ''}{a.gapPct}%
                      </div>
                      <div className="text-[10px] text-gray-400">gap</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ── Regime banner (used inside AllocationTab + as standalone) ────────────────
const REGIME_STYLE = {
  Expansion:   { bg: 'bg-emerald-50',  border: 'border-emerald-200', text: 'text-emerald-800', dot: 'bg-emerald-500'  },
  Bull:        { bg: 'bg-green-50',    border: 'border-green-200',   text: 'text-green-800',   dot: 'bg-green-500'    },
  'Late Bull': { bg: 'bg-amber-50',    border: 'border-amber-200',   text: 'text-amber-800',   dot: 'bg-amber-400'    },
  Correction:  { bg: 'bg-orange-50',   border: 'border-orange-200',  text: 'text-orange-800',  dot: 'bg-orange-400'   },
  Bear:        { bg: 'bg-red-50',      border: 'border-red-200',     text: 'text-red-800',     dot: 'bg-red-500'      },
  Recovery:    { bg: 'bg-blue-50',     border: 'border-blue-200',    text: 'text-blue-800',    dot: 'bg-blue-400'     },
  Neutral:     { bg: 'bg-gray-50',     border: 'border-gray-200',    text: 'text-gray-700',    dot: 'bg-gray-400'     },
}

const RegimeBanner = ({ regimeData }) => {
  if (!regimeData) return null
  const r = regimeData.regime ?? 'Neutral'
  const s = REGIME_STYLE[r] ?? REGIME_STYLE.Neutral
  return (
    <div className={`rounded-xl border p-3 flex flex-wrap items-start gap-3 ${s.bg} ${s.border}`}>
      <div className="flex items-center gap-2">
        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${s.dot} animate-pulse`} />
        <span className={`text-sm font-bold ${s.text}`}>{r}</span>
        <span className={`text-[11px] px-1.5 py-0.5 rounded border font-medium ${s.bg} ${s.border} ${s.text}`}>
          {regimeData.confidence} confidence
        </span>
      </div>
      <p className={`text-xs flex-1 ${s.text} opacity-80`}>{regimeData.description}</p>
      {regimeData.signals && (
        <div className="flex flex-wrap gap-3 text-[11px] text-gray-500 w-full mt-1">
          <span>Nifty: <b>{regimeData.signals.price?.toLocaleString('en-IN')}</b></span>
          {regimeData.signals.sma50  && <span>50DMA: <b className={regimeData.signals.aboveSma50  ? 'text-emerald-600' : 'text-red-500'}>{regimeData.signals.sma50.toLocaleString('en-IN')}</b></span>}
          {regimeData.signals.sma200 && <span>200DMA: <b className={regimeData.signals.aboveSma200 ? 'text-emerald-600' : 'text-red-500'}>{regimeData.signals.sma200.toLocaleString('en-IN')}</b></span>}
          {regimeData.signals.return20d  != null && <span>20d: <b className={regimeData.signals.return20d  >= 0 ? 'text-emerald-600' : 'text-red-500'}>{regimeData.signals.return20d > 0 ? '+' : ''}{regimeData.signals.return20d}%</b></span>}
          {regimeData.signals.volatility20d != null && <span>Vol: <b>{regimeData.signals.volatility20d}%</b></span>}
          {regimeData.signals.nearAllTimeHigh && <span className="text-emerald-600 font-medium">Near 52w high</span>}
          {regimeData.asOf && <span className="ml-auto text-gray-400">as of {regimeData.asOf}</span>}
        </div>
      )}
    </div>
  )
}

// ── Simulator tab ─────────────────────────────────────────────────────────────
const VERDICT_STYLE = {
  PROCEED:  { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  CONSIDER: { bg: 'bg-blue-50',   text: 'text-blue-700',    border: 'border-blue-200'    },
  CAUTION:  { bg: 'bg-amber-50',  text: 'text-amber-700',   border: 'border-amber-200'   },
  AVOID:    { bg: 'bg-red-50',    text: 'text-red-700',     border: 'border-red-200'     },
}

const SimulatorTab = ({ holdings }) => {
  const [moves, setMoves]     = useState([{ fromSchemeCode: '', toSchemeCode: '', amount: '' }])
  const [result, setResult]   = useState(null)
  const [running, setRunning] = useState(false)
  const [searchResults, setSearchResults] = useState({})
  const [searching, setSearching] = useState({})

  const heldOptions = holdings.filter(h => h.schemeCode).map(h => ({
    value: h.schemeCode, label: h.schemeName || h.fundName,
  }))

  const searchFunds = async (idx, query) => {
    if (query.length < 3) return
    setSearching(s => ({ ...s, [idx]: true }))
    try {
      const r = await mfIntelAPI.searchSchemes({ q: query, planType: 'Direct', limit: 8 })
      setSearchResults(s => ({ ...s, [idx]: r.data?.schemes ?? [] }))
    } catch {}
    finally { setSearching(s => ({ ...s, [idx]: false })) }
  }

  const updateMove = (idx, field, value) => {
    setMoves(ms => ms.map((m, i) => i === idx ? { ...m, [field]: value } : m))
  }

  const addMove = () => setMoves(ms => [...ms, { fromSchemeCode: '', toSchemeCode: '', amount: '' }])
  const removeMove = (idx) => setMoves(ms => ms.filter((_, i) => i !== idx))

  const runSimulation = async () => {
    const valid = moves.filter(m => m.fromSchemeCode && m.toSchemeCode && Number(m.amount) > 0)
    if (!valid.length) { toast.error('Fill in at least one complete move'); return }
    setRunning(true)
    setResult(null)
    try {
      const r = await mfIntelAPI.simulate(valid.map(m => ({ ...m, amount: Number(m.amount) })))
      setResult(r.data)
    } catch (err) {
      toast.error(err.response?.data?.error ?? 'Simulation failed')
    } finally { setRunning(false) }
  }

  return (
    <div className="space-y-5">
      <p className="text-xs text-gray-500 px-1">
        Model the impact of a fund switch before taking action — see return, drawdown,
        health, exposure, and fragility changes side-by-side.
      </p>

      {/* Move builder */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-4">
        <p className="text-sm font-semibold text-gray-700">Proposed Moves</p>
        {moves.map((move, idx) => (
          <div key={idx} className="flex flex-wrap gap-2 items-start pb-3 border-b border-gray-50 last:border-0 last:pb-0">
            <div className="flex-1 min-w-[200px]">
              <p className="text-[11px] text-gray-500 mb-1">Move FROM (currently held)</p>
              <select value={move.fromSchemeCode} onChange={e => updateMove(idx, 'fromSchemeCode', e.target.value)}
                className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
                <option value="">Select fund…</option>
                {heldOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <p className="text-[11px] text-gray-500 mb-1">Move TO (search any fund)</p>
              <input
                type="text"
                placeholder="Type fund name to search…"
                className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5"
                onChange={e => { updateMove(idx, 'toSchemeName', e.target.value); searchFunds(idx, e.target.value) }}
              />
              {searchResults[idx]?.length > 0 && (
                <div className="mt-1 border border-gray-200 rounded-lg shadow-sm bg-white max-h-40 overflow-y-auto z-10">
                  {searchResults[idx].map(s => (
                    <button key={s.schemeCode}
                      onClick={() => { updateMove(idx, 'toSchemeCode', s.schemeCode); setSearchResults(sr => ({ ...sr, [idx]: [] })) }}
                      className="w-full text-left px-2 py-1.5 text-xs hover:bg-primary-50 border-b border-gray-50 last:border-0">
                      <span className="font-medium text-gray-800 block truncate">{s.schemeName}</span>
                      <span className="text-gray-400">{s.amc}</span>
                    </button>
                  ))}
                </div>
              )}
              {move.toSchemeCode && <p className="text-[11px] text-emerald-600 mt-0.5">✓ {move.toSchemeCode}</p>}
            </div>
            <div className="w-32">
              <p className="text-[11px] text-gray-500 mb-1">Amount (₹)</p>
              <input type="number" value={move.amount} onChange={e => updateMove(idx, 'amount', e.target.value)}
                placeholder="500000" className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5" />
            </div>
            {moves.length > 1 && (
              <button onClick={() => removeMove(idx)} className="mt-5 text-gray-300 hover:text-red-400 text-lg">×</button>
            )}
          </div>
        ))}
        <div className="flex gap-2">
          <button onClick={addMove} className="text-xs text-primary-600 hover:text-primary-700 font-medium">+ Add another move</button>
          <button onClick={runSimulation} disabled={running}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
            <Zap className={`w-4 h-4 ${running ? 'animate-pulse' : ''}`} />
            {running ? 'Simulating…' : 'Run Simulation'}
          </button>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Overall verdict */}
          {result.overallVerdict && (() => {
            const v = VERDICT_STYLE[result.overallVerdict] ?? VERDICT_STYLE.CONSIDER
            return (
              <div className={`rounded-xl border p-3 flex items-center gap-3 ${v.bg} ${v.border}`}>
                <div className="text-2xl">
                  {result.overallVerdict === 'PROCEED' ? '✅' : result.overallVerdict === 'CONSIDER' ? '🔵' : result.overallVerdict === 'CAUTION' ? '⚠️' : '❌'}
                </div>
                <div>
                  <p className={`text-sm font-bold ${v.text}`}>{result.overallVerdict}</p>
                  <p className="text-xs text-gray-600">Overall recommendation across all proposed moves</p>
                </div>
              </div>
            )
          })()}

          {/* Fragility delta */}
          {result.fragilityBefore && result.fragilityAfter && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Portfolio Fragility Impact</p>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className={`text-2xl font-bold ${scoreColor(result.fragilityBefore.score)}`}>{Math.round(result.fragilityBefore.score)}</div>
                  <div className="text-[11px] text-gray-400">Before · {result.fragilityBefore.label}</div>
                </div>
                <div className="text-gray-300 text-xl">→</div>
                <div className="text-center">
                  <div className={`text-2xl font-bold ${scoreColor(result.fragilityAfter.score)}`}>{Math.round(result.fragilityAfter.score)}</div>
                  <div className="text-[11px] text-gray-400">After · {result.fragilityAfter.label}</div>
                </div>
                {result.fragilityAfter.delta != null && (
                  <div className={`ml-auto text-lg font-bold ${result.fragilityAfter.delta >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {result.fragilityAfter.delta >= 0 ? '+' : ''}{result.fragilityAfter.delta.toFixed(1)}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Per-move results */}
          {result.moves?.map((m, i) => {
            const v = VERDICT_STYLE[m.verdict?.recommendation] ?? VERDICT_STYLE.CONSIDER
            return (
              <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className={`h-1 w-full ${m.verdict?.recommendation === 'PROCEED' ? 'bg-emerald-400' : m.verdict?.recommendation === 'AVOID' ? 'bg-red-400' : m.verdict?.recommendation === 'CAUTION' ? 'bg-amber-400' : 'bg-blue-400'}`} />
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Switching {fmtCr(m.amount)}</p>
                      <p className="text-sm font-semibold text-gray-800">{m.fromSchemeName} <span className="text-gray-300">→</span> {m.toSchemeName}</p>
                      {(m.categoryChange || m.amcChange) && (
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {m.categoryChange && `Category: ${m.categoryChange.from} → ${m.categoryChange.to}`}
                          {m.amcChange && ` · AMC: ${m.amcChange.from} → ${m.amcChange.to}`}
                        </p>
                      )}
                    </div>
                    <span className={`text-xs font-bold px-2 py-1 rounded-lg border ${v.bg} ${v.text} ${v.border}`}>{m.verdict?.recommendation}</span>
                  </div>

                  {/* Impact grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                    {[
                      { label: '1Y Return', from: m.returnImpact?.from1Y, to: m.returnImpact?.to1Y, delta: m.returnImpact?.delta1Y, unit: '%', higherBetter: true },
                      { label: 'Composite Score', from: m.scoreImpact?.fromComposite, to: m.scoreImpact?.toComposite, delta: m.scoreImpact?.delta, unit: '', higherBetter: true },
                      { label: 'Cat. Rank %ile', from: m.rankImpact?.from1YPct, to: m.rankImpact?.to1YPct, delta: m.rankImpact?.deltaPercentile, unit: '%', higherBetter: true },
                      { label: 'Max Drawdown', from: m.drawdownImpact?.fromMaxDD, to: m.drawdownImpact?.toMaxDD, delta: m.drawdownImpact?.delta, unit: '%', higherBetter: false },
                      { label: 'Health Score', from: m.healthImpact?.fromScore, to: m.healthImpact?.toScore, delta: m.healthImpact?.delta, unit: '', higherBetter: true },
                      { label: 'Est. Annual Gain/Loss', from: null, to: null, delta: m.returnImpact?.estimatedAnnualGainLoss, unit: '₹', higherBetter: true, rawDelta: true },
                    ].map(({ label, from, to, delta, unit, higherBetter, rawDelta }) => (
                      <div key={label} className="bg-gray-50 rounded-lg p-2">
                        <p className="text-[10px] text-gray-400">{label}</p>
                        {!rawDelta && from != null && to != null ? (
                          <p className="text-xs text-gray-700">{unit === '₹' ? fmtCr(from) : `${from}${unit}`} → <span className="font-semibold">{unit === '₹' ? fmtCr(to) : `${to}${unit}`}</span></p>
                        ) : null}
                        {delta != null && (
                          <p className={`text-sm font-bold ${
                            (higherBetter ? delta > 0 : delta < 0) ? 'text-emerald-600' : delta === 0 ? 'text-gray-400' : 'text-red-500'
                          }`}>
                            {delta > 0 ? '+' : ''}{unit === '₹' ? fmtCr(Math.abs(delta)) : `${delta}${unit}`}
                            {unit === '₹' && delta < 0 ? ' (less)' : ''}
                          </p>
                        )}
                        {delta == null && from == null && <p className="text-xs text-gray-300">No data</p>}
                      </div>
                    ))}
                  </div>

                  {/* Verdict reasons */}
                  {(m.verdict?.positives?.length > 0 || m.verdict?.negatives?.length > 0) && (
                    <div className="flex flex-wrap gap-1.5">
                      {m.verdict.positives?.map((p, j) => (
                        <span key={j} className="text-[11px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full">✓ {p}</span>
                      ))}
                      {m.verdict.negatives?.map((n, j) => (
                        <span key={j} className="text-[11px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full">✗ {n}</span>
                      ))}
                    </div>
                  )}

                  {/* Exposure changes */}
                  {m.exposureChanges?.length > 0 && (
                    <div className="mt-3 border-t border-gray-50 pt-2">
                      <p className="text-[11px] text-gray-400 mb-1.5">Top stock exposure changes (₹)</p>
                      <div className="flex flex-wrap gap-1.5">
                        {m.exposureChanges.slice(0, 8).map((e, j) => (
                          <span key={j} className={`text-[11px] px-1.5 py-0.5 rounded-full border ${
                            e.direction === 'INCREASE' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-red-50 text-red-600 border-red-100'
                          }`}>
                            {e.stockSymbol} {e.direction === 'INCREASE' ? '+' : ''}{fmtCr(e.deltaValue)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {!m.exposureDataAvailable && (
                    <p className="mt-2 text-[11px] text-gray-400">Exposure changes: import portfolio CSVs in Exposure tab for detailed stock-level impact</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Phase 17 — Research Lab ────────────────────────────────────────────────────

const ResearchScoreBar = ({ value, max = 100, color = 'bg-primary-500' }) => (
  <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
    <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max(0, Math.min(100, (value ?? 0) / max * 100))}%` }} />
  </div>
)

const LoadBtn = ({ onClick, loading, label = 'Load' }) => (
  <button
    onClick={onClick}
    disabled={loading}
    className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition-colors flex items-center gap-1.5"
  >
    <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
    {loading ? 'Loading…' : label}
  </button>
)

const ResearchSection = ({ title, icon: Icon, children, onLoad, loading, loaded }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-primary-600" />}
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      </div>
      {onLoad && <LoadBtn onClick={onLoad} loading={loading} label={loaded ? 'Refresh' : 'Load'} />}
    </div>
    <div className="p-4">{children}</div>
  </div>
)

const ResearchTab = () => {
  const [summary,      setSummary]      = useState(null)
  const [amcs,         setAmcs]         = useState(null)
  const [categories,   setCategories]   = useState(null)
  const [resilience,   setResilience]   = useState(null)
  const [beaters,      setBeaters]      = useState(null)
  const [distribution, setDistribution] = useState(null)
  const [expense,      setExpense]      = useState(null)
  const [persistence,  setPersistence]  = useState(null)

  const [loadingSummary,     setLoadingSummary]     = useState(true)
  const [loadingAmcs,        setLoadingAmcs]        = useState(false)
  const [loadingCategories,  setLoadingCategories]  = useState(false)
  const [loadingResilience,  setLoadingResilience]  = useState(false)
  const [loadingBeaters,     setLoadingBeaters]     = useState(false)
  const [loadingDistrib,     setLoadingDistrib]     = useState(false)
  const [loadingExpense,     setLoadingExpense]      = useState(false)
  const [loadingPersistence, setLoadingPersistence] = useState(false)

  // Auto-load everything on mount — no manual "Load" buttons needed
  useEffect(() => {
    setLoadingSummary(true); setLoadingDistrib(true)
    setLoadingAmcs(true); setLoadingCategories(true)
    setLoadingResilience(true); setLoadingBeaters(true)
    setLoadingExpense(true); setLoadingPersistence(true)
    Promise.allSettled([
      mfIntelAPI.researchSummary().then(r => setSummary(r.data)),
      mfIntelAPI.researchDistribution().then(r => setDistribution(r.data)),
      mfIntelAPI.researchAMC().then(r => setAmcs(r.data?.amcs ?? [])),
      mfIntelAPI.researchCategories().then(r => setCategories(r.data?.categories ?? [])),
      mfIntelAPI.researchResilience().then(r => setResilience(r.data?.funds ?? [])),
      mfIntelAPI.researchBeaters().then(r => setBeaters(r.data?.funds ?? [])),
      mfIntelAPI.researchExpense().then(r => setExpense(r.data)),
      mfIntelAPI.researchPersistence().then(r => setPersistence(r.data)),
    ]).finally(() => {
      setLoadingSummary(false); setLoadingDistrib(false)
      setLoadingAmcs(false); setLoadingCategories(false)
      setLoadingResilience(false); setLoadingBeaters(false)
      setLoadingExpense(false); setLoadingPersistence(false)
    })
  }, [])

  const loadAmcs       = () => { setLoadingAmcs(true);        mfIntelAPI.researchAMC().then(r => setAmcs(r.data?.amcs ?? [])).catch(() => setAmcs([])).finally(() => setLoadingAmcs(false)) }
  const loadCategories = () => { setLoadingCategories(true);  mfIntelAPI.researchCategories().then(r => setCategories(r.data?.categories ?? [])).catch(() => setCategories([])).finally(() => setLoadingCategories(false)) }
  const loadResilience = () => { setLoadingResilience(true);  mfIntelAPI.researchResilience().then(r => setResilience(r.data?.funds ?? [])).catch(() => setResilience([])).finally(() => setLoadingResilience(false)) }
  const loadBeaters    = () => { setLoadingBeaters(true);     mfIntelAPI.researchBeaters().then(r => setBeaters(r.data?.funds ?? [])).catch(() => setBeaters([])).finally(() => setLoadingBeaters(false)) }
  const loadExpense    = () => { setLoadingExpense(true);     mfIntelAPI.researchExpense().then(r => setExpense(r.data)).catch(() => {}).finally(() => setLoadingExpense(false)) }
  const loadPersistence = () => {
    setLoadingPersistence(true)
    mfIntelAPI.researchPersistence().then(r => setPersistence(r.data)).catch(() => {}).finally(() => setLoadingPersistence(false))
  }

  return (
    <div className="space-y-5">

      {/* Summary header */}
      {loadingSummary && <div className="flex justify-center py-8"><LoadingSpinner /></div>}
      {summary && !loadingSummary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCard title="Schemes Scored"   value={summary.totalScored ?? '—'}      sub={`${summary.scoringCoverage ?? '—'}% of universe`} icon={BookOpen} />
          <SummaryCard title="Good Quality"     value={summary.totalGood ?? '—'}        sub={`${summary.totalPartial ?? 0} partial`}           icon={CheckCircle} />
          <SummaryCard title="Universe Avg"     value={summary.avgComposite != null ? Math.round(summary.avgComposite) : '—'} sub="composite score" icon={BarChart2} />
          <SummaryCard title="Top AMC"          value={summary.topAMC?.amc ?? '—'}      sub={summary.topAMC ? `avg ${summary.topAMC.avgScore}` : undefined} icon={Award} />
        </div>
      )}

      {summary?.topFund && (
        <div className="bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-100 rounded-xl p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs text-primary-600 uppercase tracking-wide font-semibold">Top Fund in Universe</p>
            <p className="font-semibold text-gray-900 mt-0.5 truncate max-w-xs" title={summary.topFund.schemeName}>{summary.topFund.schemeName}</p>
            <p className="text-xs text-gray-500 mt-0.5">{summary.topFund.amc} · {summary.topFund.category}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className={`text-2xl font-bold ${scoreColor(summary.topFund.compositeScore ?? 0)}`}>{summary.topFund.compositeScore ?? '—'}</p>
            <p className="text-xs text-gray-400">composite</p>
          </div>
        </div>
      )}

      {/* Score Distribution */}
      <ResearchSection title="Score Distribution" icon={BarChart2} loading={loadingDistrib} loaded={!!distribution}>
        {distribution?.buckets?.length ? (
          <div>
            <div className="flex items-end gap-1 h-28">
              {distribution.buckets.map((b) => (
                <div key={b.label} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[9px] text-gray-400">{b.count}</span>
                  <div
                    className={`w-full rounded-t-sm transition-all ${b.lo >= 70 ? 'bg-emerald-400' : b.lo >= 45 ? 'bg-amber-400' : 'bg-red-400'}`}
                    style={{ height: `${Math.max(4, b.heightPct ?? 0)}%` }}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-1 mt-1">
              {distribution.buckets.map(b => (
                <div key={b.label} className="flex-1 text-center text-[9px] text-gray-400">{b.lo}</div>
              ))}
            </div>
            <div className="flex gap-4 mt-3 text-xs text-gray-500">
              <span>P25: <strong className="text-gray-700">{distribution.p25}</strong></span>
              <span>Median: <strong className="text-gray-700">{distribution.median}</strong></span>
              <span>P75: <strong className="text-gray-700">{distribution.p75}</strong></span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-4">Score data not yet available. Run analysis first.</p>
        )}
      </ResearchSection>

      {/* AMC Leaderboard */}
      <ResearchSection title="AMC Leaderboard" icon={Award} loading={loadingAmcs} loaded={!!amcs}>
        {!amcs && !loadingAmcs && (
          <p className="text-sm text-gray-400 text-center py-4">Loading AMC data…</p>
        )}
        {loadingAmcs && <div className="flex justify-center py-8"><LoadingSpinner /></div>}
        {amcs?.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 text-gray-500 font-semibold uppercase">#</th>
                  <th className="text-left py-2 text-gray-500 font-semibold uppercase">AMC</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">Funds</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">Avg Score</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">Top 25%</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">Sharpe</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">Consistency</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">Drawdown</th>
                  <th className="text-left py-2 text-gray-500 font-semibold uppercase pl-4">Best Fund</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {amcs.map((a, i) => (
                  <tr key={a.amc} className="hover:bg-gray-50 transition-colors">
                    <td className="py-2.5 text-gray-400 w-6">{i + 1}</td>
                    <td className="py-2.5 font-medium text-gray-800 max-w-[140px] truncate" title={a.amc}>{a.amc}</td>
                    <td className="py-2.5 text-right text-gray-600">{a.fundCount}</td>
                    <td className="py-2.5 text-right">
                      <span className={`font-semibold ${scoreColor(a.avgCompositeScore ?? 0)}`}>{a.avgCompositeScore ?? '—'}</span>
                    </td>
                    <td className="py-2.5 text-right text-gray-600">{a.topQuartileRatio != null ? `${a.topQuartileRatio}%` : '—'}</td>
                    <td className="py-2.5 text-right text-gray-600">{a.avgSharpeScore ?? '—'}</td>
                    <td className="py-2.5 text-right text-gray-600">{a.avgConsistencyScore ?? '—'}</td>
                    <td className="py-2.5 text-right text-gray-600">{a.avgDrawdownScore ?? '—'}</td>
                    <td className="py-2.5 pl-4 text-gray-500 max-w-[160px] truncate" title={a.topFund?.schemeName}>{a.topFund?.schemeName ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {amcs?.length === 0 && !loadingAmcs && (
          <p className="text-sm text-gray-400 text-center py-4">No AMC data yet. Score some funds first.</p>
        )}
      </ResearchSection>

      {/* Category Insights */}
      <ResearchSection title="Category Insights" icon={Target} loading={loadingCategories} loaded={!!categories}>
        {!categories && !loadingCategories && (
          <p className="text-sm text-gray-400 text-center py-4">Loading category data…</p>
        )}
        {loadingCategories && <div className="flex justify-center py-8"><LoadingSpinner /></div>}
        {categories?.length > 0 && (
          <div className="space-y-3">
            {categories.map((c) => (
              <div key={c.category} className="border border-gray-100 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-sm font-semibold text-gray-800">{c.category}</span>
                    <span className="ml-2 text-xs text-gray-400">{c.fundCount} funds</span>
                    {c.dominantSubCategories?.[0] && (
                      <span className="ml-2 text-[11px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{c.dominantSubCategories[0].name}</span>
                    )}
                  </div>
                  <span className={`text-lg font-bold ${scoreColor(c.avgCompositeScore ?? 0)}`}>{c.avgCompositeScore ?? '—'}</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center mt-1">
                  {[
                    { label: '1Y Return', val: c.avgReturns1YScore },
                    { label: 'Sharpe',   val: c.avgSharpeScore },
                    { label: 'Consist.', val: c.avgConsistencyScore },
                    { label: 'Drawdown', val: c.avgDrawdownScore },
                  ].map(({ label, val }) => (
                    <div key={label}>
                      <div className="text-[10px] text-gray-400">{label}</div>
                      <div className={`text-xs font-semibold ${scoreColor(val ?? 0)}`}>{val ?? '—'}</div>
                      <ResearchScoreBar value={val} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        {categories?.length === 0 && !loadingCategories && (
          <p className="text-sm text-gray-400 text-center py-4">No category data yet.</p>
        )}
      </ResearchSection>

      {/* Downside Resilience */}
      <ResearchSection title="Downside Resilience Leaders" icon={ShieldCheck} loading={loadingResilience} loaded={!!resilience}>
        {!resilience && !loadingResilience && (
          <p className="text-sm text-gray-400 text-center py-4">No GOOD-quality scores yet (need ≥3 years of NAV history).</p>
        )}
        {loadingResilience && <div className="flex justify-center py-8"><LoadingSpinner /></div>}
        {resilience?.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 text-gray-500 font-semibold uppercase">#</th>
                  <th className="text-left py-2 text-gray-500 font-semibold uppercase">Fund</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">Resilience</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">Drawdown↓</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">Consistency</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">Max DD%</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">1Y Return</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">3Y CAGR</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {resilience.map((f, i) => (
                  <tr key={f.schemeCode} className="hover:bg-gray-50 transition-colors">
                    <td className="py-2.5 text-gray-400 w-6">{i + 1}</td>
                    <td className="py-2.5">
                      <div className="font-medium text-gray-800 truncate max-w-[200px]" title={f.schemeName}>{f.schemeName}</div>
                      <div className="text-[10px] text-gray-400">{f.amc} · {f.category}</div>
                    </td>
                    <td className="py-2.5 text-right">
                      <span className={`font-bold ${scoreColor(f.resilienceScore ?? 0)}`}>{f.resilienceScore ?? '—'}</span>
                    </td>
                    <td className="py-2.5 text-right text-gray-600">{f.drawdownScore ?? '—'}</td>
                    <td className="py-2.5 text-right text-gray-600">{f.consistencyScore ?? '—'}</td>
                    <td className="py-2.5 text-right text-red-500">{f.maxDrawdownPct != null ? `−${f.maxDrawdownPct}%` : '—'}</td>
                    <td className={`py-2.5 text-right font-medium ${gainColor(f.return1Y)}`}>{f.return1Y != null ? `${f.return1Y > 0 ? '+' : ''}${f.return1Y}%` : '—'}</td>
                    <td className={`py-2.5 text-right ${gainColor(f.return3YCAGR)}`}>{f.return3YCAGR != null ? `${f.return3YCAGR > 0 ? '+' : ''}${f.return3YCAGR}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {resilience?.length === 0 && !loadingResilience && (
          <p className="text-sm text-gray-400 text-center py-4">No GOOD-quality scores yet (need ≥3 years of NAV history).</p>
        )}
      </ResearchSection>

      {/* Benchmark Beaters */}
      <ResearchSection title="Consistent Benchmark Beaters" icon={TrendingUp} loading={loadingBeaters} loaded={!!beaters}>
        {!beaters && !loadingBeaters && (
          <p className="text-sm text-gray-400 text-center py-4">No GOOD-quality scores yet (need ≥3 years of NAV history).</p>
        )}
        {loadingBeaters && <div className="flex justify-center py-8"><LoadingSpinner /></div>}
        {beaters?.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 text-gray-500 font-semibold uppercase">#</th>
                  <th className="text-left py-2 text-gray-500 font-semibold uppercase">Fund</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">Beater Score</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">Cat Rank</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">Sharpe</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">1Y Return</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">3Y CAGR</th>
                  <th className="text-right py-2 text-gray-500 font-semibold uppercase">Sharpe Ratio</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {beaters.map((f, i) => (
                  <tr key={f.schemeCode} className="hover:bg-gray-50 transition-colors">
                    <td className="py-2.5 text-gray-400 w-6">{i + 1}</td>
                    <td className="py-2.5">
                      <div className="font-medium text-gray-800 truncate max-w-[200px]" title={f.schemeName}>{f.schemeName}</div>
                      <div className="text-[10px] text-gray-400">{f.amc} · {f.category}</div>
                    </td>
                    <td className="py-2.5 text-right">
                      <span className={`font-bold ${scoreColor(f.beaterScore ?? 0)}`}>{f.beaterScore ?? '—'}</span>
                    </td>
                    <td className="py-2.5 text-right text-gray-600">{f.categoryRankScore ?? '—'}</td>
                    <td className="py-2.5 text-right text-gray-600">{f.sharpeScore ?? '—'}</td>
                    <td className={`py-2.5 text-right font-medium ${gainColor(f.return1Y)}`}>{f.return1Y != null ? `${f.return1Y > 0 ? '+' : ''}${f.return1Y}%` : '—'}</td>
                    <td className={`py-2.5 text-right ${gainColor(f.return3YCAGR)}`}>{f.return3YCAGR != null ? `${f.return3YCAGR > 0 ? '+' : ''}${f.return3YCAGR}%` : '—'}</td>
                    <td className="py-2.5 text-right text-gray-600">{f.sharpeRatio ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {beaters?.length === 0 && !loadingBeaters && (
          <p className="text-sm text-gray-400 text-center py-4">No GOOD-quality scores yet (need ≥3 years of NAV history).</p>
        )}
      </ResearchSection>

      {/* Expense Ratio Correlation */}
      <ResearchSection title="Expense Ratio vs Performance" icon={Info} loading={loadingExpense} loaded={!!expense}>
        {!expense && !loadingExpense && (
          <p className="text-sm text-gray-400 text-center py-4">Loading expense ratio data…</p>
        )}
        {loadingExpense && <div className="flex justify-center py-8"><LoadingSpinner /></div>}
        {expense && (
          expense.note ? (
            <div className="text-center py-6">
              <Info className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">{expense.note}</p>
              <p className="text-xs text-gray-400 mt-1">Coverage: {expense.coveredCount} / {expense.totalScored} funds have expense ratio data</p>
            </div>
          ) : (
            <div>
              <p className="text-xs text-gray-400 mb-3">Coverage: {expense.coveredCount} / {expense.totalScored} funds ({expense.coverageRatio}%) have expense ratio data</p>
              <div className="space-y-2.5">
                {expense.bins.filter(b => b.fundCount > 0).map((b) => {
                  const maxScore = Math.max(...expense.bins.map(x => x.avgCompositeScore ?? 0), 1)
                  return (
                    <div key={b.label} className="flex items-center gap-3">
                      <div className="w-20 text-xs text-gray-600 font-medium">{b.label}</div>
                      <div className="text-[10px] text-gray-400 w-20">{b.range}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                            <div
                              className={`h-full rounded ${(b.avgCompositeScore ?? 0) >= 70 ? 'bg-emerald-400' : (b.avgCompositeScore ?? 0) >= 45 ? 'bg-amber-400' : 'bg-red-400'}`}
                              style={{ width: `${maxScore > 0 ? (b.avgCompositeScore ?? 0) / maxScore * 100 : 0}%` }}
                            />
                          </div>
                          <span className={`text-xs font-semibold w-8 text-right ${scoreColor(b.avgCompositeScore ?? 0)}`}>{b.avgCompositeScore ?? '—'}</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 w-12 text-right">{b.fundCount} funds</div>
                    </div>
                  )
                })}
              </div>
              {expense.bins.every(b => b.fundCount === 0) && (
                <p className="text-sm text-gray-400 text-center py-4">No bins have data — expense ratio not yet populated.</p>
              )}
            </div>
          )
        )}
      </ResearchSection>

      {/* Persistence Research */}
      <ResearchSection title="AMC Rank Persistence" icon={Clock} loading={loadingPersistence} loaded={!!persistence}>
        {!persistence && !loadingPersistence && (
          <p className="text-sm text-gray-400 text-center py-4">Loading persistence data…</p>
        )}
        {loadingPersistence && <div className="flex justify-center py-8"><LoadingSpinner /></div>}
        {persistence && (
          persistence.note ? (
            <div className="text-center py-6">
              <Clock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">{persistence.note}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">By AMC ({persistence.monthsCovered} months)</h4>
                <div className="space-y-1.5">
                  {(persistence.amcPersistence ?? []).slice(0, 10).map((a, i) => (
                    <div key={a.amc} className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-400 w-4">{i + 1}</span>
                      <span className="text-xs text-gray-700 flex-1 truncate">{a.amc}</span>
                      <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${scoreBg(a.topQRatio ?? 0)}`} style={{ width: `${a.topQRatio ?? 0}%` }} />
                      </div>
                      <span className={`text-xs font-semibold w-10 text-right ${scoreColor(a.topQRatio ?? 0)}`}>{a.topQRatio}%</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">By Category</h4>
                <div className="space-y-1.5">
                  {(persistence.categoryPersistence ?? []).slice(0, 10).map((c, i) => (
                    <div key={c.category} className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-400 w-4">{i + 1}</span>
                      <span className="text-xs text-gray-700 flex-1 truncate">{c.category}</span>
                      <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${scoreBg(c.topQRatio ?? 0)}`} style={{ width: `${c.topQRatio ?? 0}%` }} />
                      </div>
                      <span className={`text-xs font-semibold w-10 text-right ${scoreColor(c.topQRatio ?? 0)}`}>{c.topQRatio}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )
        )}
      </ResearchSection>

    </div>
  )
}

// ── Phase 18 — Annual Rotation Engine ────────────────────────────────────────

const CONFIDENCE_STYLE = {
  HIGH:   'bg-red-100 text-red-700 border-red-200',
  MEDIUM: 'bg-amber-100 text-amber-700 border-amber-200',
  LOW:    'bg-blue-100 text-blue-700 border-blue-200',
  '—':    'bg-gray-100 text-gray-500 border-gray-200',
}

const ACTION_COLOR = {
  ROTATE:  'text-red-600',
  CONSIDER:'text-amber-600',
  MONITOR: 'text-blue-600',
  HOLD:    'text-gray-400',
}

const RotationTab = () => {
  const [plan,       setPlan]       = useState(null)
  const [loading,    setLoading]    = useState(false)
  const [generating, setGenerating] = useState(false)
  const [expanded,   setExpanded]   = useState({})

  // Auto-load on mount: try existing plan first, generate if none
  useEffect(() => {
    setLoading(true)
    mfIntelAPI.getRotationPlan()
      .then(r => {
        const existing = r.data?.plan ?? null
        if (existing) { setPlan(existing); setLoading(false) }
        else {
          // No existing plan — auto-generate
          setGenerating(true)
          mfIntelAPI.generateRotation()
            .then(gr => setPlan(gr.data?.plan ?? null))
            .catch(() => {})
            .finally(() => { setGenerating(false); setLoading(false) })
        }
      })
      .catch(() => setLoading(false))
  }, [])

  const generate = async () => {
    setGenerating(true)
    try {
      const r = await mfIntelAPI.generateRotation()
      setPlan(r.data?.plan ?? null)
      toast.success('Rotation plan updated')
    } catch (err) {
      toast.error(err.response?.data?.error ?? 'Generation failed')
    } finally {
      setGenerating(false)
    }
  }

  const toggleExpand = (code) => setExpanded(e => ({ ...e, [code]: !e[code] }))

  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Annual Rotation Engine</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Review monthly. Rotate annually. Only HIGH confidence switches warrant action.
          </p>
          {plan?.generatedAt && (
            <p className="text-xs text-gray-400 mt-0.5">Generated: {new Date(plan.generatedAt).toLocaleDateString('en-IN')}</p>
          )}
        </div>
        <button
          onClick={generate}
          disabled={generating}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors whitespace-nowrap"
        >
          <RotateCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
          {generating ? 'Analysing…' : plan ? 'Refresh' : 'Generate Plan'}
        </button>
      </div>

      {/* Summary chips */}
      {plan && (
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'HOLD',    count: plan.holdCount,     color: 'bg-gray-100 text-gray-600' },
            { label: 'MONITOR', count: plan.monitorCount,  color: 'bg-blue-100 text-blue-700' },
            { label: 'CONSIDER',count: plan.considerCount, color: 'bg-amber-100 text-amber-700' },
            { label: 'ROTATE',  count: plan.rotateCount,   color: 'bg-red-100 text-red-700' },
          ].map(({ label, count, color }) => (
            <span key={label} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${color}`}>
              {count} {label}
            </span>
          ))}
          {(plan.highConfidenceCount ?? 0) > 0 && (
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-red-600 text-white">
              {plan.highConfidenceCount} HIGH confidence
            </span>
          )}
        </div>
      )}

      {!plan && !loading && (
        <div className="text-center py-16 text-gray-400">
          <RotateCw className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No rotation plan yet</p>
          <p className="text-sm mt-1">Click Generate Plan to analyse your holdings</p>
        </div>
      )}

      {/* Items */}
      {plan?.items?.map((item) => (
        <div key={item.fromSchemeCode} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
          item.action === 'ROTATE' ? 'border-red-200' :
          item.action === 'CONSIDER' ? 'border-amber-200' :
          item.action === 'MONITOR' ? 'border-blue-200' : 'border-gray-100'
        }`}>
          <div className="px-4 py-3 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-800 truncate max-w-xs" title={item.fromSchemeName}>{item.fromSchemeName}</span>
                <span className={`text-xs font-bold ${ACTION_COLOR[item.action] ?? 'text-gray-500'}`}>{item.action}</span>
                {item.confidence !== '—' && (
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${CONFIDENCE_STYLE[item.confidence]}`}>{item.confidence}</span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                <span>{item.fromAMC}</span>
                <span>·</span>
                <span>{item.fromCategory}</span>
                <span>·</span>
                <span>Score: <strong className={`${scoreColor(item.fromComposite ?? 0)}`}>{item.fromComposite ?? '—'}</strong></span>
                {item.fromHealthLabel && <span>· <span className={`font-medium ${item.fromHealthLabel === 'Healthy' ? 'text-emerald-600' : item.fromHealthLabel === 'Replace' ? 'text-red-500' : 'text-amber-500'}`}>{item.fromHealthLabel}</span></span>}
              </div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {item.reasons?.map((r, i) => (
                  <span key={i} className="text-[10px] bg-gray-50 text-gray-500 px-1.5 py-0.5 rounded border border-gray-100">{r}</span>
                ))}
              </div>
            </div>
            {item.candidates?.length > 0 && (
              <button onClick={() => toggleExpand(item.fromSchemeCode)} className="text-xs text-primary-600 hover:text-primary-800 whitespace-nowrap flex items-center gap-1 mt-0.5">
                {expanded[item.fromSchemeCode] ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {item.candidates.length} candidate{item.candidates.length > 1 ? 's' : ''}
              </button>
            )}
          </div>

          {/* Best candidate preview */}
          {item.bestCandidateName && item.action !== 'HOLD' && (
            <div className="px-4 pb-2 flex items-center gap-2">
              <span className="text-[11px] text-gray-400">Best alternative:</span>
              <span className="text-xs font-medium text-gray-700 truncate">{item.bestCandidateName}</span>
              {item.bestScoreDiff != null && item.bestScoreDiff > 0 && (
                <span className="text-[11px] text-emerald-600 font-semibold">+{item.bestScoreDiff} pts</span>
              )}
            </div>
          )}

          {/* Expanded candidate table */}
          {expanded[item.fromSchemeCode] && item.candidates?.length > 0 && (
            <div className="border-t border-gray-50 px-4 py-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-1.5 text-gray-400 font-medium">Candidate</th>
                    <th className="text-right py-1.5 text-gray-400 font-medium">Score</th>
                    <th className="text-right py-1.5 text-gray-400 font-medium">Diff</th>
                    <th className="text-right py-1.5 text-gray-400 font-medium">Health</th>
                    <th className="text-right py-1.5 text-gray-400 font-medium">TopQ Months</th>
                    <th className="text-right py-1.5 text-gray-400 font-medium">1Y Return</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {item.candidates.map(c => (
                    <tr key={c.schemeCode} className="hover:bg-gray-50">
                      <td className="py-2">
                        <div className="font-medium text-gray-700 truncate max-w-[180px]" title={c.schemeName}>{c.schemeName}</div>
                        <div className="text-[10px] text-gray-400">{c.amc}</div>
                      </td>
                      <td className="py-2 text-right"><span className={`font-semibold ${scoreColor(c.compositeScore ?? 0)}`}>{c.compositeScore ?? '—'}</span></td>
                      <td className={`py-2 text-right font-semibold ${(c.scoreDiff ?? 0) > 0 ? 'text-emerald-600' : 'text-red-500'}`}>{c.scoreDiff != null ? (c.scoreDiff > 0 ? '+' : '') + c.scoreDiff : '—'}</td>
                      <td className={`py-2 text-right ${c.healthLabel === 'Healthy' ? 'text-emerald-600' : c.healthLabel === 'Replace' ? 'text-red-500' : 'text-amber-500'}`}>{c.healthLabel ?? '—'}</td>
                      <td className="py-2 text-right text-gray-600">{c.monthsTopQuartile ?? 0}/6</td>
                      <td className={`py-2 text-right ${gainColor(c.return1Y)}`}>{c.return1Y != null ? `${c.return1Y > 0 ? '+' : ''}${c.return1Y}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── Phase 19 — Monthly Investment Committee ────────────────────────────────────

const VERDICT_BADGE = {
  GREEN: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  AMBER: 'bg-amber-100 text-amber-800 border-amber-200',
  RED:   'bg-red-100 text-red-800 border-red-200',
}

const CommitteeTab = () => {
  const [summary,    setSummary]    = useState(null)
  const [history,    setHistory]    = useState([])
  const [loading,    setLoading]    = useState(false)
  const [generating, setGenerating] = useState(false)

  useEffect(() => {
    setLoading(true)
    Promise.all([
      mfIntelAPI.getCommitteeLatest().catch(() => ({ data: { summary: null } })),
      mfIntelAPI.getCommitteeHistory().catch(() => ({ data: { history: [] } })),
    ]).then(([latestRes, histRes]) => {
      const existing = latestRes.data?.summary ?? null
      setHistory(histRes.data?.history ?? [])
      if (existing) { setSummary(existing); setLoading(false) }
      else {
        // No summary for this month yet — auto-generate
        setGenerating(true)
        mfIntelAPI.generateCommittee()
          .then(r => {
            setSummary(r.data?.summary ?? null)
            mfIntelAPI.getCommitteeHistory().then(hr => setHistory(hr.data?.history ?? [])).catch(() => {})
          })
          .catch(() => {})
          .finally(() => { setGenerating(false); setLoading(false) })
      }
    }).catch(() => setLoading(false))
  }, [])

  const generate = async () => {
    setGenerating(true)
    try {
      const r = await mfIntelAPI.generateCommittee()
      setSummary(r.data?.summary ?? null)
      // Refresh history
      mfIntelAPI.getCommitteeHistory().then(hr => setHistory(hr.data?.history ?? [])).catch(() => {})
      toast.success('Committee summary generated')
    } catch (err) {
      toast.error(err.response?.data?.error ?? 'Generation failed')
    } finally {
      setGenerating(false)
    }
  }

  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Monthly Investment Committee</h3>
          <p className="text-xs text-gray-400 mt-0.5">Synthesises all phases into one structured monthly review</p>
          {summary?.generatedAt && (
            <p className="text-xs text-gray-400 mt-0.5">
              {summary.month} · Generated: {new Date(summary.generatedAt).toLocaleDateString('en-IN')}
            </p>
          )}
        </div>
        <button
          onClick={generate}
          disabled={generating}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors whitespace-nowrap"
        >
          <Calendar className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
          {generating ? 'Generating…' : summary ? 'Regenerate' : 'Generate Report'}
        </button>
      </div>

      {!summary && !loading && (
        <div className="text-center py-16 text-gray-400">
          <Calendar className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No committee report yet</p>
          <p className="text-sm mt-1">Generate your first monthly report above</p>
        </div>
      )}

      {summary && (
        <>
          {/* Verdict banner */}
          <div className={`rounded-xl border p-4 ${VERDICT_BADGE[summary.verdict] ?? 'bg-gray-100 text-gray-700 border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-bold uppercase tracking-wide">Committee Verdict: {summary.verdict}</span>
                <p className="text-sm mt-1 leading-relaxed">{summary.executiveSummary}</p>
              </div>
            </div>
          </div>

          {/* Top stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCard
              title="Portfolio"
              value={summary.portfolioValue != null ? `₹${(summary.portfolioValue / 100).toFixed(1)}L` : '—'}
              sub={summary.gainLossPct != null ? pct(summary.gainLossPct) : undefined}
              color={gainColor(summary.gainLossPct)}
              icon={TrendingUp}
            />
            <SummaryCard
              title="Health Status"
              value={`${summary.healthCounts?.Healthy ?? 0} Healthy`}
              sub={`${summary.healthCounts?.Replace ?? 0} Replace, ${summary.healthCounts?.Weak ?? 0} Weak`}
              icon={ShieldCheck}
            />
            <SummaryCard
              title="Regime"
              value={summary.regime ?? '—'}
              sub={summary.fragilityLabel ? `Fragility: ${summary.fragilityLabel}` : undefined}
              icon={BarChart2}
            />
            <SummaryCard
              title="Fragility"
              value={summary.fragilityScore != null ? Math.round(summary.fragilityScore) : '—'}
              sub={`${summary.fragilityLabel ?? ''} risk`}
              color={summary.fragilityScore >= 50 ? 'text-red-600' : summary.fragilityScore >= 30 ? 'text-amber-600' : 'text-emerald-600'}
              icon={AlertTriangle}
            />
          </div>

          {/* Action Items */}
          {summary.actionItems?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Action Items</h3>
              <ol className="space-y-2">
                {summary.actionItems.map((item, i) => (
                  <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                    <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${i === 0 ? 'text-red-500' : i === 1 ? 'text-amber-500' : 'text-gray-300'}`} />
                    <span>{item.replace(/^\d+\.\s*/, '')}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Decisions Required */}
          {summary.decisionsRequired?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-50">
                <h3 className="text-sm font-semibold text-gray-800">Decisions Required</h3>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-4 py-2 text-gray-500 font-semibold">Fund</th>
                    <th className="text-center px-4 py-2 text-gray-500 font-semibold">Action</th>
                    <th className="text-center px-4 py-2 text-gray-500 font-semibold">Urgency</th>
                    <th className="text-left px-4 py-2 text-gray-500 font-semibold">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {summary.decisionsRequired.map((d, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-800 truncate max-w-[200px]" title={d.schemeName}>{d.schemeName}</td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`text-xs font-bold ${d.action === 'REPLACE' ? 'text-red-600' : d.action === 'REDUCE' ? 'text-amber-600' : 'text-blue-600'}`}>{d.action}</span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${d.urgency === 'HIGH' ? 'bg-red-100 text-red-700' : d.urgency === 'MEDIUM' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>{d.urgency}</span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 max-w-xs truncate" title={d.reason}>{d.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Rotation Opportunities */}
          {summary.rotationOpportunities?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-50">
                <h3 className="text-sm font-semibold text-gray-800">Rotation Opportunities</h3>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-4 py-2 text-gray-500 font-semibold">From</th>
                    <th className="text-left px-4 py-2 text-gray-500 font-semibold">To</th>
                    <th className="text-right px-4 py-2 text-gray-500 font-semibold">Score Δ</th>
                    <th className="text-center px-4 py-2 text-gray-500 font-semibold">Confidence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {summary.rotationOpportunities.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-700 truncate max-w-[180px]" title={r.fromSchemeName}>{r.fromSchemeName}</td>
                      <td className="px-4 py-2.5 font-medium text-gray-700 truncate max-w-[180px]" title={r.toSchemeName}>{r.toSchemeName}</td>
                      <td className="px-4 py-2.5 text-right"><span className="font-semibold text-emerald-600">+{r.scoreDiff}</span></td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${CONFIDENCE_STYLE[r.confidence] ?? 'bg-gray-100 text-gray-500'}`}>{r.confidence}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Performance Review */}
          {(summary.topPerformers?.length > 0 || summary.laggards?.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {summary.topPerformers?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                  <h3 className="text-sm font-semibold text-emerald-700 mb-2">Top Performers</h3>
                  <div className="space-y-2">
                    {summary.topPerformers.map((f, i) => (
                      <div key={i} className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium text-gray-800 truncate" title={f.schemeName}>{f.schemeName}</div>
                          <div className="text-[10px] text-gray-400">{f.category}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className={`text-xs font-bold ${scoreColor(f.compositeScore ?? 0)}`}>{f.compositeScore ?? '—'}</div>
                          {f.return1Y != null && <div className={`text-[10px] font-semibold ${gainColor(f.return1Y)}`}>{f.return1Y > 0 ? '+' : ''}{f.return1Y}%</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {summary.laggards?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                  <h3 className="text-sm font-semibold text-red-600 mb-2">Laggards</h3>
                  <div className="space-y-2">
                    {summary.laggards.map((f, i) => (
                      <div key={i} className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium text-gray-800 truncate" title={f.schemeName}>{f.schemeName}</div>
                          <div className="text-[10px] text-gray-400">{f.category}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className={`text-xs font-bold ${scoreColor(f.compositeScore ?? 0)}`}>{f.compositeScore ?? '—'}</div>
                          {f.return1Y != null && <div className={`text-[10px] font-semibold ${gainColor(f.return1Y)}`}>{f.return1Y > 0 ? '+' : ''}{f.return1Y}%</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Risk Flags */}
          {summary.riskFlags?.length > 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" /> Risk Flags
              </h3>
              <div className="flex flex-wrap gap-2">
                {summary.riskFlags.map(f => (
                  <span key={f} className="text-xs bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full font-medium border border-amber-200">
                    {f.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* History */}
          {history.length > 1 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-50">
                <h3 className="text-sm font-semibold text-gray-800">Committee History</h3>
              </div>
              <div className="divide-y divide-gray-50">
                {history.map((h) => (
                  <div key={h.month} className="px-4 py-2.5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-gray-700">{h.month}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${VERDICT_BADGE[h.verdict] ?? ''}`}>{h.verdict}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>{h.holdingCount} funds</span>
                      <span>{h.regime ?? '—'}</span>
                      {h.gainLossPct != null && <span className={gainColor(h.gainLossPct)}>{h.gainLossPct > 0 ? '+' : ''}{Number(h.gainLossPct).toFixed(1)}%</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── Investment Plan Tab ───────────────────────────────────────────────────────

const CONFIDENCE_BADGE = {
  HIGH:   'bg-emerald-100 text-emerald-700 border-emerald-200',
  MEDIUM: 'bg-amber-100 text-amber-700 border-amber-200',
  LOW:    'bg-gray-100 text-gray-500 border-gray-200',
}

const URGENCY_COLOR = { HIGH: 'text-red-600', MEDIUM: 'text-amber-600', LOW: 'text-gray-500' }

const fmtL = (v) => v == null ? '—' : `₹${(v / 100000).toFixed(2)}L`

const PlanTab = () => {
  const [plan,        setPlan]        = useState(null)
  const [loading,     setLoading]     = useState(false)
  const [riskProfile, setRiskProfile] = useState('Moderate')
  const [regime,      setRegime]      = useState('auto')

  const load = (rp = 'Moderate', reg = 'auto') => {
    setLoading(true)
    mfIntelAPI.getInvestmentPlan({ riskProfile: rp, regime: reg })
      .then(r => setPlan(r.data ?? null))
      .catch(err => { toast.error(err.response?.data?.error ?? 'Plan failed'); setPlan(null) })
      .finally(() => setLoading(false))
  }

  // Auto-load on mount with defaults
  useEffect(() => { load('Moderate', 'auto') }, []) // eslint-disable-line

  const totalSell = plan?.summary?.sellTotal ?? 0
  const totalBuy  = plan?.summary?.buyTotal  ?? 0

  return (
    <div className="space-y-4">

      {/* Controls */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Risk Profile</label>
            <select
              value={riskProfile}
              onChange={e => setRiskProfile(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              <option>Conservative</option>
              <option>Moderate</option>
              <option>Aggressive</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Market Regime</label>
            <select
              value={regime}
              onChange={e => setRegime(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              <option value="auto">Auto-detect</option>
              <option>Bull</option>
              <option>Neutral</option>
              <option>Correction</option>
              <option>Bear</option>
              <option>Recovery</option>
            </select>
          </div>
          <button
            onClick={() => load(riskProfile, regime)}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
          >
            <Lightbulb className={`w-4 h-4 ${loading ? 'animate-pulse' : ''}`} />
            {loading ? 'Computing…' : 'Refresh Plan'}
          </button>
        </div>
      </div>

      {!plan && !loading && (
        <div className="text-center py-16 text-gray-400">
          <Lightbulb className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium text-gray-600">Your personalised investment plan</p>
          <p className="text-sm mt-1">Choose a risk profile and click Generate Plan</p>
          <p className="text-xs mt-2 text-gray-400 max-w-sm mx-auto">
            Fund-specific SELL / BUY / HOLD actions with ₹ amounts. Every recommendation is derived from your actual portfolio data.
          </p>
        </div>
      )}

      {loading && <div className="flex justify-center py-16"><LoadingSpinner /></div>}

      {plan && (
        <>
          {/* Snapshot row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCard
              title="Portfolio"
              value={fmtL(plan.totalValue)}
              sub={plan.gainLossPct != null ? pct(plan.gainLossPct) : undefined}
              color={gainColor(plan.gainLossPct)}
              icon={BarChart2}
            />
            <SummaryCard
              title="Regime"
              value={plan.regime ?? '—'}
              sub={plan.regimeDesc ? plan.regimeDesc.split('.')[0] : undefined}
              icon={TrendingUp}
            />
            <SummaryCard
              title="Fragility"
              value={plan.fragility?.label ?? '—'}
              sub={plan.fragility?.score != null ? `${Math.round(plan.fragility.score)}/100` : undefined}
              color={plan.fragility?.score >= 50 ? 'text-red-600' : plan.fragility?.score >= 30 ? 'text-amber-600' : 'text-emerald-600'}
              icon={AlertTriangle}
            />
            <SummaryCard
              title="Plan Confidence"
              value={plan.planConfidence ?? '—'}
              sub={`${plan.summary.exitCount + plan.summary.reduceCount} sell · ${plan.summary.buyCount} buy · ${plan.summary.holdCount} hold`}
              icon={CheckCircle}
            />
          </div>

          {/* Alignment & Fragility improvement */}
          {(plan.alignment?.before != null || plan.fragility?.scoreAfterPlan != null) && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 grid grid-cols-2 gap-6">
              {plan.alignment?.before != null && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Allocation Alignment</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${scoreBg(plan.alignment.before ?? 0)}`} style={{ width: `${plan.alignment.before ?? 0}%` }} />
                    </div>
                    <span className={`text-sm font-bold ${scoreColor(plan.alignment.before ?? 0)}`}>{plan.alignment.before}%</span>
                    {plan.alignment.after != null && plan.alignment.after > plan.alignment.before && (
                      <>
                        <span className="text-gray-300">→</span>
                        <span className="text-sm font-bold text-emerald-600">{plan.alignment.after}%</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">How close your current allocation is to the {plan.riskProfile} target</p>
                </div>
              )}
              {plan.fragility?.score != null && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Fragility Risk</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${plan.fragility.score >= 50 ? 'bg-red-400' : plan.fragility.score >= 30 ? 'bg-amber-400' : 'bg-emerald-400'}`} style={{ width: `${plan.fragility.score ?? 0}%` }} />
                    </div>
                    <span className={`text-sm font-bold ${plan.fragility.score >= 50 ? 'text-red-600' : 'text-amber-600'}`}>{Math.round(plan.fragility.score)}</span>
                    {plan.fragility.scoreAfterPlan != null && plan.fragility.scoreAfterPlan < plan.fragility.score && (
                      <>
                        <span className="text-gray-300">→</span>
                        <span className="text-sm font-bold text-emerald-600">{Math.round(plan.fragility.scoreAfterPlan)}</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Lower fragility = more resilient portfolio</p>
                </div>
              )}
            </div>
          )}

          {/* SELL actions */}
          {plan.sellActions?.length > 0 && (
            <div className="bg-white rounded-xl border border-red-100 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-red-50 bg-red-50">
                <div className="flex items-center gap-2">
                  <ArrowDownCircle className="w-4 h-4 text-red-600" />
                  <h3 className="text-sm font-semibold text-red-800">
                    SELL Actions — {plan.sellActions.length} fund{plan.sellActions.length > 1 ? 's' : ''} · Free up {fmtL(totalSell)}
                  </h3>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${CONFIDENCE_BADGE[plan.planConfidence] ?? ''}`}>{plan.planConfidence}</span>
              </div>
              <div className="divide-y divide-gray-50">
                {plan.sellActions.map((a, i) => (
                  <div key={`${a.schemeCode}-${i}`} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded ${a.type === 'EXIT' ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}`}>{a.type}</span>
                          <span className={`text-xs font-semibold ${URGENCY_COLOR[a.urgency] ?? ''}`}>{a.urgency}</span>
                          <span className="text-sm font-semibold text-gray-800 truncate max-w-xs" title={a.schemeName}>{a.schemeName}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                          <span>{a.amc}</span>
                          <span>·</span>
                          <span className={`font-medium ${a.healthLabel === 'Healthy' ? 'text-emerald-600' : a.healthLabel === 'Replace' ? 'text-red-600' : 'text-amber-500'}`}>{a.healthLabel}</span>
                          {a.compositeScore != null && <span>· Score: <strong className={scoreColor(a.compositeScore)}>{a.compositeScore}</strong></span>}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{a.reason}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-base font-bold text-red-700">{fmtL(a.reduceBy)}</p>
                        <p className="text-[10px] text-gray-400">
                          {a.exitFully ? 'Full exit' : `of ${fmtL(a.currentValue)}`}
                        </p>
                        {!a.exitFully && <p className="text-[10px] text-gray-400">Keep {fmtL(a.remainingAfter)}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* BUY actions */}
          {plan.buyActions?.length > 0 && (
            <div className="bg-white rounded-xl border border-emerald-100 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-emerald-50 bg-emerald-50">
                <div className="flex items-center gap-2">
                  <ArrowUpCircle className="w-4 h-4 text-emerald-600" />
                  <h3 className="text-sm font-semibold text-emerald-800">
                    BUY Actions — {plan.buyActions.length} categor{plan.buyActions.length > 1 ? 'ies' : 'y'} · Deploy {fmtL(totalBuy)}
                  </h3>
                </div>
              </div>
              <div className="divide-y divide-gray-50">
                {plan.buyActions.map((a, i) => (
                  <div key={i} className="px-4 py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold px-2 py-0.5 rounded bg-emerald-600 text-white">INVEST</span>
                        <span className={`text-xs font-semibold ${URGENCY_COLOR[a.urgency] ?? ''}`}>{a.urgency}</span>
                        <span className="text-sm font-semibold text-gray-800">{a.subCategory || a.category}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Current {a.currentPct}% → Target {a.targetPct}% · Gap: +{a.gapPct}%
                      </p>
                      {a.suggestion && (
                        <div className="mt-1.5 flex items-center gap-2">
                          <span className="text-[10px] text-gray-400">Suggested fund:</span>
                          <span className="text-xs font-medium text-gray-700 truncate max-w-[220px]" title={a.suggestion.schemeName}>{a.suggestion.schemeName}</span>
                          <span className={`text-[11px] font-semibold ${scoreColor(a.suggestion.compositeScore ?? 0)}`}>{a.suggestion.compositeScore}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-base font-bold text-emerald-700">{fmtL(a.deployAmount)}</p>
                      <p className="text-[10px] text-gray-400">to deploy</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* HOLD list */}
          {plan.holdFunds?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-700">HOLD — {plan.holdFunds.length} fund{plan.holdFunds.length > 1 ? 's' : ''} within target range</h3>
              </div>
              <div className="px-4 py-2 flex flex-wrap gap-2">
                {plan.holdFunds.map(f => (
                  <div key={f.schemeCode} className="flex items-center gap-1.5 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5">
                    <span className="text-xs font-medium text-gray-700 max-w-[160px] truncate" title={f.schemeName}>{f.schemeName}</span>
                    <span className={`text-[11px] font-semibold ${scoreColor(f.compositeScore ?? 0)}`}>{f.compositeScore ?? '—'}</span>
                    <span className={`text-[10px] ${f.healthLabel === 'Healthy' ? 'text-emerald-600' : 'text-gray-400'}`}>{f.healthLabel}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No actions needed */}
          {plan.sellActions?.length === 0 && plan.buyActions?.length === 0 && (
            <div className="text-center py-8 bg-white rounded-xl border border-emerald-100 shadow-sm">
              <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
              <p className="font-semibold text-emerald-700">Portfolio is well-aligned</p>
              <p className="text-sm text-gray-500 mt-1">
                No funds need exiting and allocation is within ±4% of target. No action required this month.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── Universe Tab — top 200 funds for comparison + fund picking ────────────────

const HEALTH_DOT = {
  Healthy: 'bg-emerald-500',
  Watch:   'bg-amber-400',
  Weak:    'bg-orange-400',
  Replace: 'bg-red-500',
}

const UniverseTab = ({ category, setCategory, planType, setPlanType,
                       search, setSearch, funds, loading, error, onLoad,
                       onScoreUniverse, scoring }) => {
  const [dbCheck, setDbCheck] = useState(null)   // { schemeCount, scoreCount }
  const [syncing, setSyncing] = useState(false)
  const [syncMsg, setSyncMsg] = useState('')

  // Auto-check DB state on mount
  useEffect(() => {
    mfIntelAPI.universeCheck()
      .then(r => setDbCheck(r.data))
      .catch(() => setDbCheck({ schemeCount: 0, scoreCount: 0, error: 'Could not reach server' }))
  }, [])

  // Refresh DB check after scoring finishes
  useEffect(() => {
    if (!scoring) {
      mfIntelAPI.universeCheck()
        .then(r => setDbCheck(r.data))
        .catch(() => {})
    }
  }, [scoring])

  // Direct universe sync (bypasses Full Analysis)
  const doSync = async () => {
    setSyncing(true); setSyncMsg('Downloading AMFI scheme universe (~60 s)…')
    try {
      const r = await mfIntelAPI.syncUniverse()
      const count = r.data?.total ?? '?'
      setSyncMsg(`Done ✓ — ${count} schemes synced.`)
      setDbCheck(d => ({ ...d, schemeCount: count }))
      onLoad(category, planType)
    } catch (err) {
      setSyncMsg('❌ Sync failed: ' + (err.response?.data?.error ?? err.message))
    } finally { setSyncing(false) }
  }

  const filtered = (funds ?? []).filter(f => {
    if (!search) return true
    const q = search.toLowerCase()
    return f.schemeName?.toLowerCase().includes(q) || f.amc?.toLowerCase().includes(q) || f.subCategory?.toLowerCase().includes(q)
  })

  const scoredCount   = filtered.filter(f => f.scored).length
  const CATEGORIES = ['All','Equity','Debt','Hybrid','Gold','Solution Oriented']

  return (
    <div className="space-y-4">

      {/* Top action bar — Score Universe CTA */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-800">Market Universe — Top 200 Funds</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Curated from India's top AMCs · Direct plans · Ranked by composite score · Your holdings excluded
            </p>
            {scoredCount > 0 && !scoring && (
              <p className="text-xs text-emerald-600 font-medium mt-1">
                {scoredCount} funds ranked · Re-score to refresh rankings
              </p>
            )}
          </div>
          <button
            onClick={onScoreUniverse}
            disabled={scoring || loading}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            <Globe className={`w-4 h-4 ${scoring ? 'animate-pulse' : ''}`} />
            {scoring ? 'Scoring…' : scoredCount > 0 ? 'Re-score Universe' : 'Score Universe'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1 flex-wrap">
            {CATEGORIES.map(c => (
              <button
                key={c}
                onClick={() => { setCategory(c); onLoad(c, planType) }}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  category === c ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >{c}</button>
            ))}
          </div>
          <select
            value={planType}
            onChange={e => { setPlanType(e.target.value); onLoad(category, e.target.value) }}
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700"
          >
            <option value="All">All Plans</option>
            <option value="Direct">Direct Only</option>
            <option value="Regular">Regular Only</option>
          </select>
          <input
            type="text"
            placeholder="Search fund name, AMC, or sub-category…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 min-w-[200px] text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        {funds?.length > 0 && (
          <p className="text-xs text-gray-500">
            <span className="font-semibold text-primary-700">{scoredCount}</span> scored &amp; ranked ·{' '}
            <span className="text-gray-400">{filtered.length} shown</span>
            {scoredCount === 0 && !scoring && (
              <span className="ml-2 text-amber-600 font-medium">Click "Score Universe" to rank funds ↑</span>
            )}
          </p>
        )}
      </div>

      {loading && <div className="flex justify-center py-16"><LoadingSpinner /></div>}

      {/* DB diagnostic strip */}
      {dbCheck && (
        <div className={`rounded-xl border px-4 py-3 text-sm flex flex-wrap items-center gap-3 ${
          dbCheck.schemeCount > 0 ? 'bg-gray-50 border-gray-200' : 'bg-amber-50 border-amber-200'
        }`}>
          <span className="text-gray-600">
            DB: <strong className={dbCheck.schemeCount > 0 ? 'text-gray-900' : 'text-amber-700'}>{dbCheck.schemeCount?.toLocaleString() ?? '?'}</strong> schemes ·{' '}
            <strong>{dbCheck.scoreCount?.toLocaleString() ?? '?'}</strong> scored
          </span>
          {dbCheck.schemeCount < 100 && (
            <button
              onClick={doSync}
              disabled={syncing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing…' : 'Sync Scheme List First'}
            </button>
          )}
          {syncMsg && <span className={`text-xs font-medium ${syncMsg.startsWith('❌') ? 'text-red-600' : 'text-emerald-700'}`}>{syncMsg}</span>}
          {dbCheck.error && <span className="text-xs text-red-500">⚠ {dbCheck.error}</span>}
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm">
          <p className="font-semibold text-red-800 mb-1">⚠ Failed to load universe</p>
          <p className="text-red-700 font-mono text-xs break-all">{error}</p>
        </div>
      )}

      {!loading && !funds?.length && !error && (
        <div className="text-center py-16 text-gray-400">
          <Globe className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p className="font-semibold text-gray-600 text-base">No funds scored yet</p>
          <p className="text-sm mt-2 text-gray-500 max-w-sm mx-auto">
            Click <strong className="text-primary-600">Score Universe</strong> above to score the top 200 Indian mutual funds across all major AMCs. Takes about 2–3 minutes.
          </p>
          {dbCheck?.schemeCount < 100 && (
            <p className="text-xs mt-3 text-amber-600">First sync the scheme list using the DB strip above, then score.</p>
          )}
        </div>
      )}

      {!loading && funds?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Column legend — clarifies that last 3 cols are 0-100 scores, not raw values */}
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-3 text-[10px] text-gray-400">
            <span className="font-semibold text-gray-500 uppercase tracking-wide">Score key</span>
            <span>1Y&nbsp;Ret / 3Y&nbsp;CAGR = actual&nbsp;%&nbsp;return</span>
            <span className="text-gray-200">|</span>
            <span>Sharpe&nbsp;S / DD&nbsp;Prot = <strong className="text-gray-500">0–100 score</strong> (higher = better)</span>
            <span className="text-gray-200">|</span>
            <span>Sharpe&nbsp;S:&nbsp;— = not applicable (ultra-low-volatility fund)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="border-b border-gray-100">
                  <th className="text-left px-3 py-2.5 text-gray-500 font-semibold uppercase w-8">#</th>
                  <th className="text-left px-3 py-2.5 text-gray-500 font-semibold uppercase">Fund</th>
                  <th className="text-right px-3 py-2.5 text-gray-500 font-semibold uppercase">Score</th>
                  <th className="text-right px-3 py-2.5 text-gray-500 font-semibold uppercase">1Y Ret</th>
                  <th className="text-right px-3 py-2.5 text-gray-500 font-semibold uppercase">3Y CAGR</th>
                  <th className="text-right px-3 py-2.5 text-gray-500 font-semibold uppercase" title="Sharpe Score: 0–100 (not the raw Sharpe ratio). — = N/A for liquid/money-market funds.">Sharpe S</th>
                  <th className="text-right px-3 py-2.5 text-gray-500 font-semibold uppercase" title="Drawdown Protection Score: 0–100. 99 means near-zero max drawdown (e.g. liquid fund). NOT a percentage loss.">DD Prot</th>
                  <th className="text-center px-3 py-2.5 text-gray-500 font-semibold uppercase">Health</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((f) => {
                  const unscored = !f.scored
                  // Cap extreme returns for display (segregated recoveries can be 10000%+)
                  const disp1Y    = f.return1Y    != null ? Math.max(-200, Math.min(200, f.return1Y))    : null
                  const disp3YCAGR= f.return3YCAGR != null ? Math.max(-50,  Math.min(100, f.return3YCAGR)) : null
                  return (
                    <tr key={f.schemeCode} className={`hover:bg-gray-50 transition-colors ${unscored ? 'opacity-50' : ''}`}>
                      <td className="px-3 py-2.5 text-gray-400">{unscored ? '—' : f.rank}</td>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-gray-800 max-w-[240px] truncate" title={f.schemeName}>{f.schemeName}</div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {f.amc} · {f.subCategory || f.category}
                          {unscored && <span className="ml-1 text-amber-500 font-medium">· not scored</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {unscored ? (
                          <span className="text-gray-300 italic">—</span>
                        ) : (
                          <div className="flex items-center justify-end gap-1.5">
                            <div className="w-12 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${scoreBg(f.compositeScore ?? 0)}`} style={{ width: `${f.compositeScore ?? 0}%` }} />
                            </div>
                            <span className={`font-bold ${scoreColor(f.compositeScore ?? 0)}`}>{f.compositeScore ?? '—'}</span>
                          </div>
                        )}
                      </td>
                      <td className={`px-3 py-2.5 text-right font-medium ${gainColor(disp1Y)}`}>
                        {disp1Y != null ? `${disp1Y > 0 ? '+' : ''}${fmt(disp1Y, 1)}%` : '—'}
                      </td>
                      <td className={`px-3 py-2.5 text-right ${gainColor(disp3YCAGR)}`}>
                        {disp3YCAGR != null ? `${disp3YCAGR > 0 ? '+' : ''}${fmt(disp3YCAGR, 1)}%` : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right text-gray-600">{f.sharpeScore ?? <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2.5 text-right text-gray-600">{f.drawdownScore ?? <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2.5 text-center">
                        {f.healthLabel ? (
                          <span className="flex items-center justify-center gap-1">
                            <span className={`w-2 h-2 rounded-full ${HEALTH_DOT[f.healthLabel] ?? 'bg-gray-300'}`} />
                            <span className={`${f.healthLabel === 'Healthy' ? 'text-emerald-600' : f.healthLabel === 'Replace' ? 'text-red-600' : 'text-amber-500'}`}>{f.healthLabel}</span>
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

const TABS = [
  { id: 'holdings',     label: 'Holdings',         icon: BarChart2 },
  { id: 'validation',   label: 'Validation',        icon: ShieldCheck },
  { id: 'audit',        label: 'Replacement Audit', icon: RefreshCw },
  { id: 'cat-ranks',    label: 'Category Rankings', icon: Award },
  { id: 'scores',       label: 'Scores',            icon: ShieldCheck },
  { id: 'opportunities',label: 'Opportunity',       icon: Zap },
  { id: 'benchmark',    label: 'Benchmark',         icon: Target },
  { id: 'health',       label: 'Health',            icon: ShieldCheck },
  { id: 'exposure',     label: 'Exposure',          icon: TrendingUp },
  { id: 'overlap',      label: 'Overlap',           icon: AlertTriangle },
  { id: 'fragility',    label: 'Fragility',         icon: AlertTriangle },
  { id: 'allocation',   label: 'Allocation',        icon: Target },
  { id: 'simulator',    label: 'Simulator',         icon: Zap },
  { id: 'research',     label: 'Research',          icon: BookOpen },
  { id: 'rotation',     label: 'Rotation',          icon: RotateCw },
  { id: 'committee',    label: 'Committee',         icon: Calendar },
  { id: 'plan',         label: 'Plan',              icon: Lightbulb },
  { id: 'universe',     label: 'Universe',          icon: Globe },
]

export default function MutualFunds() {
  const [activeTab,       setActiveTab]       = useState('holdings')
  const [holdings,        setHoldings]        = useState([])
  const [summary,         setSummary]         = useState(null)
  const [holdingScores,   setHoldingScores]   = useState([])
  const [opportunities,   setOpportunities]   = useState([])
  const [benchmark,       setBenchmark]       = useState([])
  const [holdingsHealth,  setHoldingsHealth]  = useState([])
  const [exposure,        setExposure]        = useState(null)
  const [exposureStatus,  setExposureStatus]  = useState(null)
  const [overlap,         setOverlap]         = useState(null)
  const [fragility,       setFragility]       = useState(null)
  const [allocation,      setAllocation]      = useState(null)
  const [regimeData,      setRegimeData]      = useState(null)
  const [riskProfile,     setRiskProfile]     = useState('Moderate')
  const [regime,          setRegime]          = useState('Neutral')

  const [loadingHoldings,  setLoadingHoldings]  = useState(true)
  const [loadingScores,    setLoadingScores]    = useState(false)
  const [loadingOpps,      setLoadingOpps]      = useState(false)
  const [loadingBenchmark, setLoadingBenchmark] = useState(false)
  const [loadingHealth,    setLoadingHealth]    = useState(false)
  const [loadingExposure,  setLoadingExposure]  = useState(false)
  const [loadingOverlap,   setLoadingOverlap]   = useState(false)
  const [loadingFragility,  setLoadingFragility]  = useState(false)
  const [loadingAllocation, setLoadingAllocation] = useState(false)
  const [loadingRegime,     setLoadingRegime]     = useState(false)

  // Universe state
  const [universeFunds,    setUniverseFunds]    = useState(null)
  const [universeCategory, setUniverseCategory] = useState('All')
  const [universePlanType, setUniversePlanType] = useState('All')
  const [universeSearch,   setUniverseSearch]   = useState('')
  const [loadingUniverse,  setLoadingUniverse]  = useState(false)
  const [universeError,    setUniverseError]    = useState('')
  const [universeScoring,  setUniverseScoring]  = useState(false)

  // ── Analysis state (lifted from SyncPanel so it persists across tab switches) ─
  const [analysisRunning, setAnalysisRunning] = useState(false)
  const [analysisStatus,  setAnalysisStatus]  = useState('')
  const [analysisStep,    setAnalysisStep]    = useState(0)
  const [analysisTotal,   setAnalysisTotal]   = useState(0)

  const runAnalysis = async () => {
    if (analysisRunning) return
    setAnalysisRunning(true)
    setAnalysisStatus('Checking database…')
    setAnalysisStep(0)
    try {
      // ── Step 0: Check what's actually in the DB ──────────────────────────
      const checkRes = await mfIntelAPI.universeCheck().catch(() => ({ data: { schemeCount: 0, scoreCount: 0 } }))
      const schemeCount = checkRes.data?.schemeCount ?? 0
      const needsUniverseSync = schemeCount < 100

      const total = needsUniverseSync ? 5 : 4
      setAnalysisTotal(total)
      let step = 0

      if (needsUniverseSync) {
        step++; setAnalysisStep(step)
        setAnalysisStatus(`Downloading AMFI scheme universe (~60 s)… Currently ${schemeCount} schemes in DB`)
        const syncRes = await mfIntelAPI.syncUniverse()
        const synced = syncRes.data?.total ?? '?'
        setAnalysisStatus(`Universe ready: ${synced} schemes ✓`)
        await new Promise(r => setTimeout(r, 800)) // brief pause so user sees the count
      } else {
        setAnalysisStatus(`Universe: ${schemeCount.toLocaleString()} schemes already in DB ✓`)
        await new Promise(r => setTimeout(r, 600))
      }

      step++; setAnalysisStep(step)
      setAnalysisStatus('Syncing NAV history for your funds…')
      const ownedRes = await mfIntelAPI.syncOwned()
      const navSynced = ownedRes.data?.synced ?? ownedRes.data?.schemes ?? 0
      setAnalysisStatus(`NAV history synced: ${navSynced} fund${navSynced !== 1 ? 's' : ''} ✓`)
      await new Promise(r => setTimeout(r, 400))

      step++; setAnalysisStep(step)
      setAnalysisStatus('Computing scores…')
      const holdingsRes = await mfIntelAPI.getHoldings()
      const holdings    = holdingsRes.data?.holdings ?? []
      const codes       = [...new Set(holdings.map(h => h.schemeCode).filter(Boolean))]
      if (codes.length === 0) {
        setAnalysisStatus('⚠️ No fund scheme codes found — your MF transactions may need ISINs. Scores skipped.')
        await new Promise(r => setTimeout(r, 1500))
      } else {
        let scored = 0
        for (const code of codes) { try { await mfIntelAPI.computeScore(code); scored++ } catch {} }
        setAnalysisStatus(`Scores: ${scored}/${codes.length} funds computed ✓`)
        await new Promise(r => setTimeout(r, 400))
      }

      step++; setAnalysisStep(step)
      setAnalysisStatus('Computing ranks & persistence…')
      const cats = [...new Set(holdings.map(h => h.category).filter(Boolean))]
      for (const cat of cats) { try { await mfIntelAPI.computeRanks(cat) } catch {} }
      for (const cat of cats) { try { await mfIntelAPI.buildPersistence(cat) } catch {} }

      step++; setAnalysisStep(step)
      setAnalysisStatus('Done ✓')
      toast.success('Analysis complete')
      handleSyncDone()

      setTimeout(() => { setAnalysisStatus(''); setAnalysisStep(0); setAnalysisTotal(0) }, 6000)
    } catch (err) {
      const msg = err.response?.data?.error ?? err.message ?? String(err)
      setAnalysisStatus(`❌ Error: ${msg}`)
      toast.error('Analysis failed — see status bar for details')
    } finally {
      setAnalysisRunning(false)
    }
  }

  /**
   * runUniverseScore — scores the curated top-200 universe (excluding held funds).
   *
   * Chunked approach: backend picks the full list in one fast call, then the
   * frontend sends batches of 20 codes at a time. Each batch takes ~15 s,
   * well within the 60 s HTTP timeout. Progress bar updates after every batch.
   */
  const UNIVERSE_BATCH_SIZE = 20
  const runUniverseScore = async () => {
    if (universeScoring || analysisRunning) return
    setUniverseScoring(true)
    setAnalysisRunning(true)
    setAnalysisStatus('Picking top 200 funds from major AMCs…')
    setAnalysisStep(0)
    setAnalysisTotal(1)
    try {
      // ── Step 0: purge any stale non-investable scores + ensure scheme list ────
      setAnalysisStatus('Cleaning up non-investable schemes (segregated, side-pocket)…')
      await mfIntelAPI.purgeNonInvestable().catch(() => {})   // best-effort, don't fail

      const checkRes = await mfIntelAPI.universeCheck().catch(() => ({ data: { schemeCount: 0 } }))
      if ((checkRes.data?.schemeCount ?? 0) < 100) {
        setAnalysisStatus('Downloading AMFI scheme list first (~60 s)…')
        await mfIntelAPI.syncUniverse()
      }

      // ── Step 1: get the curated code list (fast, no HTTP to MFAPI) ──────────
      setAnalysisStatus('Selecting top 200 funds across AMCs and categories…')
      const heldStr = holdings.map(h => h.schemeCode).filter(Boolean).join(',')
      const listR   = await mfIntelAPI.getCuratedCodes({ excludeCodes: heldStr })
      const codes   = listR.data?.codes ?? []
      const total   = codes.length

      if (!total) {
        setAnalysisStatus('⚠️ No curated funds found — sync the scheme list first')
        setTimeout(() => { setAnalysisStatus(''); setAnalysisStep(0); setAnalysisTotal(0) }, 5000)
        return
      }

      // ── Step 2: score in batches of 20 ──────────────────────────────────────
      const totalBatches = Math.ceil(total / UNIVERSE_BATCH_SIZE)
      setAnalysisTotal(totalBatches)
      let scoredTotal = 0, errorsTotal = 0

      for (let i = 0; i < codes.length; i += UNIVERSE_BATCH_SIZE) {
        const batch    = codes.slice(i, i + UNIVERSE_BATCH_SIZE)
        const batchNum = Math.floor(i / UNIVERSE_BATCH_SIZE) + 1
        const fromNum  = i + 1
        const toNum    = Math.min(i + UNIVERSE_BATCH_SIZE, total)
        setAnalysisStep(batchNum)
        setAnalysisStatus(`Scoring funds ${fromNum}–${toNum} of ${total} (batch ${batchNum}/${totalBatches})…`)

        const bR = await mfIntelAPI.scoreUniverseBatch({ codes: batch })
        scoredTotal  += bR.data?.scored  ?? 0
        errorsTotal  += bR.data?.errors  ?? 0
      }

      // ── Step 3: reload universe view ─────────────────────────────────────────
      setAnalysisStep(totalBatches)
      setAnalysisStatus(`${scoredTotal}/${total} funds scored ✓ — loading rankings…`)
      setLoadingUniverse(true); setUniverseError('')
      const topR = await mfIntelAPI
        .getUniverseTop({ category: universeCategory, planType: universePlanType, limit: 200, excludeCodes: heldStr })
        .catch(err => { setUniverseError(err.response?.data?.error ?? err.message); return null })
      if (topR) {
        if (topR.data?.ok === false) setUniverseError(topR.data.error || 'Unknown error')
        setUniverseFunds(topR.data?.funds ?? [])
      }
      setLoadingUniverse(false)

      setAnalysisStatus(`Done ✓ — ${scoredTotal} universe funds ranked${errorsTotal > 0 ? ` (${errorsTotal} errors)` : ''}`)
      toast.success(`Universe scored: ${scoredTotal} funds ranked`)
      setTimeout(() => { setAnalysisStatus(''); setAnalysisStep(0); setAnalysisTotal(0) }, 8000)
    } catch (err) {
      const msg = err.response?.data?.error ?? err.message ?? String(err)
      setAnalysisStatus(`❌ Error: ${msg}`)
      toast.error('Universe scoring failed — ' + msg)
      setLoadingUniverse(false)
    } finally {
      setUniverseScoring(false)
      setAnalysisRunning(false)
    }
  }

  // ── Fetch holdings (always) ────────────────────────────────────────────────
  const fetchHoldings = useCallback(async () => {
    setLoadingHoldings(true)
    try {
      const [holdRes, sumRes] = await Promise.all([
        mfIntelAPI.getHoldings(),
        mfIntelAPI.getHoldingsSummary(),
      ])
      setHoldings(holdRes.data?.holdings ?? [])
      setSummary(sumRes.data ?? null)
    } catch (err) {
      if (err.response?.status !== 401) toast.error('Failed to load holdings')
    } finally {
      setLoadingHoldings(false)
    }
  }, [])

  useEffect(() => { fetchHoldings() }, [fetchHoldings])

  // Re-trigger benchmark load when holdings arrive (benchmark tab may have been clicked before holdings loaded)
  useEffect(() => {
    if (activeTab === 'benchmark' && holdings.length && !benchmark.length && !loadingBenchmark) {
      loadBenchmarks()
    }
  }, [holdings]) // eslint-disable-line

  // ── Lazy-load on tab change ────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab === 'scores' && !holdingScores.length) {
      setLoadingScores(true)
      mfIntelAPI.holdingScores()
        .then(r => setHoldingScores(r.data?.holdings ?? []))
        .catch(() => {})
        .finally(() => setLoadingScores(false))
    }
    if (activeTab === 'opportunities' && !opportunities.length) {
      setLoadingOpps(true)
      mfIntelAPI.holdingOpportunities()
        .then(r => setOpportunities(r.data?.holdings ?? []))
        .catch(() => {})
        .finally(() => setLoadingOpps(false))
    }
    if (activeTab === 'benchmark' && !benchmark.length && holdings.length) {
      loadBenchmarks()
    }
    if (activeTab === 'health' && !holdingsHealth.length) {
      fetchHealth()
    }
    if (activeTab === 'overlap' && !overlap) {
      setLoadingOverlap(true)
      mfIntelAPI.getOverlap()
        .then(r => setOverlap(r.data ?? null))
        .catch(() => setOverlap({ empty: true }))
        .finally(() => setLoadingOverlap(false))
    }
    if (activeTab === 'fragility' && !fragility) {
      loadFragility()
    }
    if (activeTab === 'allocation' && !regimeData) {
      // auto-detect regime first, then regime setter triggers allocation load
      loadRegime()
    }
    if (activeTab === 'exposure' && !exposure) {
      setLoadingExposure(true)
      Promise.all([mfIntelAPI.getExposure(), mfIntelAPI.exposureStatus()])
        .then(([expRes, stRes]) => {
          setExposure(expRes.data ?? null)
          setExposureStatus(stRes.data?.funds ?? [])
        })
        .catch(() => {})
        .finally(() => setLoadingExposure(false))
    }
    if (activeTab === 'universe' && !universeFunds) {
      const heldStr = holdings.map(h => h.schemeCode).filter(Boolean).join(',')
      setLoadingUniverse(true); setUniverseError('')
      mfIntelAPI.getUniverseTop({ category: 'All', planType: 'All', limit: 200, excludeCodes: heldStr })
        .then(r => {
          if (r.data?.ok === false) setUniverseError(r.data.error || 'Unknown error')
          setUniverseFunds(r.data?.funds ?? [])
        })
        .catch(err => {
          setUniverseError(err.response?.data?.error ?? err.message ?? 'Network error')
          setUniverseFunds([])
        })
        .finally(() => setLoadingUniverse(false))
    }
  }, [activeTab]) // eslint-disable-line

  const fetchHealth = async () => {
    setLoadingHealth(true)
    try {
      const r = await mfIntelAPI.holdingsHealth()
      setHoldingsHealth(r.data?.holdings ?? [])
    } catch {}
    finally { setLoadingHealth(false) }
  }

  // Auto-detect regime when Allocation tab first opens
  const loadRegime = () => {
    setLoadingRegime(true)
    mfIntelAPI.getRegime()
      .then(r => {
        const data = r.data
        setRegimeData(data)
        // Auto-set the regime selector to the detected allocation regime
        if (data?.allocationRegime) setRegime(data.allocationRegime)
      })
      .catch(() => {})
      .finally(() => setLoadingRegime(false))
  }

  const loadAllocation = (profile = riskProfile, reg = regime) => {
    setLoadingAllocation(true)
    mfIntelAPI.getAllocation({ riskProfile: profile, regime: reg })
      .then(r => setAllocation(r.data ?? null))
      .catch(() => toast.error('Allocation failed'))
      .finally(() => setLoadingAllocation(false))
  }

  const loadFragility = () => {
    setLoadingFragility(true)
    mfIntelAPI.getFragility()
      .then(r => setFragility(r.data ?? null))
      .catch(() => toast.error('Fragility compute failed'))
      .finally(() => setLoadingFragility(false))
  }

  const runHealthCheck = async () => {
    setLoadingHealth(true)
    try {
      // Compute health for each holding, then reload
      const current = holdings.filter(h => h.schemeCode)
      for (const h of current) {
        try { await mfIntelAPI.computeHealth(h.schemeCode) } catch {}
      }
      const r = await mfIntelAPI.holdingsHealth()
      setHoldingsHealth(r.data?.holdings ?? [])
      toast.success('Health check complete')
    } catch (err) {
      toast.error('Health check failed')
    } finally {
      setLoadingHealth(false)
    }
  }

  const loadBenchmarks = async () => {
    if (!holdings.length) return
    setLoadingBenchmark(true)
    const results = []
    for (const h of holdings.filter(h => h.schemeCode)) {
      try {
        const r = await mfIntelAPI.benchmark(h.schemeCode)
        results.push(r.data)
      } catch {}
    }
    setBenchmark(results)
    setLoadingBenchmark(false)
  }

  const handleSyncDone = () => {
    // Clear all cached data so tabs re-fetch on next visit
    setHoldingScores([])
    setOpportunities([])
    setBenchmark([])
    setHoldingsHealth([])
    setExposure(null)
    setExposureStatus(null)
    setOverlap(null)
    setFragility(null)
    setAllocation(null)
    setUniverseFunds(null)  // force Universe tab to re-fetch with fresh scores
    fetchHoldings()
    // Immediately refresh whichever tab is currently active
    if (activeTab === 'scores')        { setLoadingScores(true);    mfIntelAPI.holdingScores().then(r => setHoldingScores(r.data?.holdings ?? [])).finally(() => setLoadingScores(false)) }
    if (activeTab === 'opportunities') { setLoadingOpps(true);      mfIntelAPI.holdingOpportunities().then(r => setOpportunities(r.data?.holdings ?? [])).finally(() => setLoadingOpps(false)) }
    if (activeTab === 'universe') {
      const heldStr = holdings.map(h => h.schemeCode).filter(Boolean).join(',')
      setLoadingUniverse(true); setUniverseError('')
      mfIntelAPI.getUniverseTop({ category: 'All', planType: 'All', limit: 200, excludeCodes: heldStr })
        .then(r => {
          if (r.data?.ok === false) setUniverseError(r.data.error || 'Unknown error')
          setUniverseFunds(r.data?.funds ?? [])
        })
        .catch(err => {
          setUniverseError(err.response?.data?.error ?? err.message ?? 'Network error')
          setUniverseFunds([])
        })
        .finally(() => setLoadingUniverse(false))
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">

      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mutual Funds</h1>
          <p className="text-sm text-gray-500 mt-0.5">Personal Investment Office · Phase 1–19 + Investment Plan</p>
        </div>
      </div>

      {/* Summary bar */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCard title="Invested" value={fmtCr(summary.totalInvested)}  icon={BarChart2} />
          <SummaryCard title="Current Value" value={fmtCr(summary.currentValue)} icon={TrendingUp} />
          <SummaryCard
            title="Total P&L"
            value={summary.gainLoss != null ? (summary.gainLoss >= 0 ? '+' : '') + fmtCr(summary.gainLoss) : '—'}
            sub={summary.gainLossPct != null ? pct(summary.gainLossPct) : undefined}
            color={gainColor(summary.gainLoss)}
            icon={summary.gainLoss >= 0 ? TrendingUp : TrendingDown}
          />
          <SummaryCard title="Funds" value={`${summary.totalHoldings ?? 0}`} sub={summary.navMissingCount ? `${summary.navMissingCount} missing NAV` : undefined} icon={Award} />
        </div>
      )}

      {/* Sync panel + analysis progress banner */}
      <SyncPanel running={analysisRunning} onRun={runAnalysis} />
      <AnalysisBanner
        running={analysisRunning}
        status={analysisStatus}
        step={analysisStep}
        total={analysisTotal}
      />

      {/* Tab bar — wraps to next line on small screens */}
      <div className="border-b border-gray-100 pb-2">
        <nav className="flex flex-wrap gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors
                ${activeTab === tab.id
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                }`}
            >
              <tab.icon className="w-3 h-3 flex-shrink-0" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'holdings'    && <HoldingsTab    holdings={holdings}          summary={summary}  loading={loadingHoldings}  />}
      {activeTab === 'validation'  && <MFPortfolioValidation />}
      {activeTab === 'audit'       && <MFReplacementAudit />}
      {activeTab === 'cat-ranks'   && <MFCategoryRankings />}
      {activeTab === 'scores'      && <ScoresTab       holdingScores={holdingScores}                  loading={loadingScores}    />}
      {activeTab === 'opportunities' && <OpportunitiesTab opportunities={opportunities}                loading={loadingOpps}      />}
      {activeTab === 'benchmark'     && (
        <div>
          {!benchmark.length && !loadingBenchmark && (
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">No benchmark data yet — run Full Analysis from the sync panel above to compute scores first.</p>
            </div>
          )}
          <BenchmarkTab holdingsBenchmark={benchmark} loading={loadingBenchmark} />
        </div>
      )}
      {activeTab === 'health' && (
        <HealthTab
          holdingsHealth={holdingsHealth}
          loading={loadingHealth}
          onRunHealth={runHealthCheck}
        />
      )}
      {activeTab === 'exposure' && (
        <ExposureTab
          exposure={exposure}
          status={exposureStatus}
          loading={loadingExposure}
          onImport={() => {
            setLoadingExposure(true)
            Promise.all([mfIntelAPI.getExposure(), mfIntelAPI.exposureStatus()])
              .then(([expRes, stRes]) => {
                setExposure(expRes.data ?? null)
                setExposureStatus(stRes.data?.funds ?? [])
              })
              .catch(() => {})
              .finally(() => setLoadingExposure(false))
          }}
        />
      )}
      {activeTab === 'overlap' && (
        <OverlapTab
          overlap={overlap}
          loading={loadingOverlap}
          onLoad={() => {
            setLoadingOverlap(true)
            mfIntelAPI.getOverlap()
              .then(r => setOverlap(r.data ?? null))
              .catch(() => toast.error('Overlap failed — import fund portfolios in Exposure tab first'))
              .finally(() => setLoadingOverlap(false))
          }}
        />
      )}
      {activeTab === 'fragility' && (
        <FragilityTab
          fragility={fragility}
          loading={loadingFragility}
          onLoad={loadFragility}
        />
      )}
      {activeTab === 'allocation' && (
        <AllocationTab
          allocation={allocation}
          loading={loadingAllocation || loadingRegime}
          riskProfile={riskProfile}
          regime={regime}
          regimeData={regimeData}
          onLoad={() => loadAllocation(riskProfile, regime)}
          onChangeProfile={(p) => { setRiskProfile(p); loadAllocation(p, regime) }}
          onChangeRegime={(r)  => { setRegime(r);      loadAllocation(riskProfile, r) }}
        />
      )}
      {activeTab === 'simulator' && (
        <SimulatorTab holdings={holdings} />
      )}
      {activeTab === 'research' && (
        <ResearchTab />
      )}
      {activeTab === 'rotation' && (
        <RotationTab />
      )}
      {activeTab === 'committee' && (
        <CommitteeTab />
      )}
      {activeTab === 'plan' && (
        <PlanTab />
      )}
      {activeTab === 'universe' && (
        <UniverseTab
          category={universeCategory}
          setCategory={setUniverseCategory}
          planType={universePlanType}
          setPlanType={setUniversePlanType}
          search={universeSearch}
          setSearch={setUniverseSearch}
          funds={universeFunds}
          loading={loadingUniverse}
          error={universeError}
          scoring={universeScoring}
          onScoreUniverse={runUniverseScore}
          onLoad={(cat, pt) => {
            const heldStr = holdings.map(h => h.schemeCode).filter(Boolean).join(',')
            setLoadingUniverse(true); setUniverseError('')
            mfIntelAPI.getUniverseTop({ category: cat, planType: pt, limit: 200, excludeCodes: heldStr })
              .then(r => {
                if (r.data?.ok === false) setUniverseError(r.data.error || 'Unknown error')
                setUniverseFunds(r.data?.funds ?? [])
              })
              .catch(err => {
                setUniverseError(err.response?.data?.error ?? err.message ?? 'Network error')
                setUniverseFunds([])
              })
              .finally(() => setLoadingUniverse(false))
          }}
        />
      )}
    </div>
  )
}
